import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getBeneficiaries, getMeetingsAttendance } from '../../API/apiService';
import medellinLogo from '../../assets/images/fo-ginf-083-alcaldia-medellin.png';
import { buildProtectedImageUrl, fetchWithAuth } from '../../utils/secureMedia';
import { getAllowedAttendanceFormatCodes } from './formatAvailability';
import './style.scss';
import { isAfroEthnicity } from './populationMarks';
import { formatMeetingTopic } from '../../utils/meetingTopic';

const ROWS_PER_SHEET = 15;
const ATTENDANCE_FORMAT_OPTIONS = [
  { value: 'FO-GINF-083', label: 'FO-GINF-083' },
  { value: 'FO-GINF-093', label: 'FO-GINF-093' },
  { value: 'FO-GINF-086', label: 'FO-GINF-086' }
];
const FO093_ROWS_PER_SHEET = 20;
const FO093_HEADER_COLUMN_WIDTHS = [
  5.29, 5.86, 24.43, 44.71, 34.29, 29.14,
  6.29, 6.29,
  6.29, 6.29, 6.29, 6.29, 6.29,
  6.43, 6.43, 6.43, 6.43, 6.43, 6.43, 6.43,
  8.29, 6.14, 17.86, 23.71, 22.86, 28.29, 20.43, 30.86
];
const FO083_COLUMN_WIDTHS = [
  6.43, 8.86, 10.43, 13, 13, 5.86, 5.29, 2.86, 7.71, 5.29,
  13, 9.29, 5.43, 6.29, 6, 9.29, 4.43, 5.29, 13, 13,
  10.86, 4.43, 5.29, 13, 13, 13, 13, 13, 13, 13,
  13, 13, 3.71, 5.29, 3.86, 5.29, 2.86, 8.14
];

const NNA_PRIVACY_NOTICE_PARTS = [
  {
    text: 'AVISO DE PRIVACIDAD PARA NIÑAS, NIÑOS Y ADOLESCENTES:',
    bold: true
  },
  {
    text: ' El Distrito Especial de Ciencia, Tecnología e Innovación de Medellín (NIT 890.905.211-1) '
  },
  {
    text: 'como responsable de tus datos personales,',
    bold: true
  },
  {
    text: ' te informa que '
  },
  {
    text: 'puede guardar y usar tus datos personales',
    bold: true
  },
  {
    text: ' -incluyendo tu número de identificación, nombres, fecha de nacimiento, Teléfono/Celular y Correo Electrónico-, porque esta información es requerida para el ejercicio de nuestras funciones legales, como pueden ser, registrar tu participación en eventos, actividades o reuniones organizadas por la Alcaldía de Medellín o permitir la comunicación sobre temas estrictamente relacionados con el cumplimiento, seguimiento y continuidad de los programas, proyectos o servicios institucionales en los que participas, garantizando la efectividad de tus derechos prevalentes. También, podrá ser transmitida a terceros encargados del tratamiento, con quienes el Distrito tenga relación contractual o legal, para cumplir con las finalidades aquí descritas y las obligaciones legales del Distrito de Medellín.\nEl Distrito de Medellín asegurará el '
  },
  {
    text: 'respeto a tus derechos prevalentes y tu interés superior',
    bold: true
  },
  {
    text: ' en todo momento, por lo que es importante que sepas que dar información sobre '
  },
  {
    text: 'datos que son sensibles (como tu firma) o responder preguntas por el hecho de ser menor de edad es completamente voluntario y ya cuenta con la autorización del adulto que te representa.',
    bold: true
  },
  {
    text: ' Tú puedes decidir no responder esas preguntas.\nDe forma gratuita puede ejercer los siguientes derechos:\n• Saber qué información tuya tenemos.\n• Conocer el permiso otorgado por el adulto que te representa\n• Pedir que la revisemos, corrijamos o borremos tus datos si están incompletos, inexactos o inducen a error\n• Presentar quejas ante la Superintendencia de Industria y Comercio (SIC).\nPuedes conocer más sobre estas reglas y los procedimientos para ejercer tus derechos en la '
  },
  {
    text: 'Política de Tratamiento de Datos Personales,',
    bold: true
  },
  {
    text: ' publicada en '
  },
  {
    text: 'www.medellin.gov.co.',
    bold: true
  }
];

const FO093_PRIVACY_NOTICE_PARTS = [
  {
    text: 'AVISO DE PRIVACIDAD Y AUTORIZACIÓN:',
    bold: true
  },
  {
    text: ' En cumplimiento de la Ley 1581 de 2012 y sus normas reglamentarias y complementarias '
  },
  {
    text: 'autorizo',
    bold: true
  },
  {
    text: ' al Distrito Especial de Ciencia, Tecnología e Innovación de Medellín, identificado con NIT 890.905.211-1 y con domicilio en el Centro Administrativo Distrital (CAD), Calle 44 N° 52–165, y contacto en la Línea Única (+57) 6044444144, como '
  },
  {
    text: 'Responsable para tratar mis datos personales',
    bold: true
  },
  {
    text: ' conforme a su Política de Tratamiento de Datos Personales Decreto 0268 de 2025 disponible en www.medellin.gov.co, con las finalidades: '
  },
  {
    text: 'Registro de asistencia a eventos, reuniones o actividades institucionales relacionadas con el cumplimiento y continuidad de los programas y proyectos o servicios Institucionales de La Secretaría de Participación Ciudadana',
    bold: true
  },
  {
    text: ' . Con la presente autorización '
  },
  {
    text: 'se recolectarán datos sensibles y/o de niñas, niños y adolescentes',
    bold: true
  },
  {
    text: ' y entiendo que su entrega es facultativa y por lo tanto '
  },
  {
    text: 'puedo abstenerme de suministrarlos',
    bold: true
  },
  {
    text: ', teniendo siempre presente el derecho a no responder este tipo de preguntas conforme a lo establecido en la Ley 1581 de 2012 y sus normas reglamentarias. '
  },
  {
    text: 'El tratamiento de los datos de las niñas, niños y adolescentes se realizará en todo momento garantizando el interés superior y los derechos prevalentes de los mismos.\n',
    bold: true
  },
  {
    text: 'La información personal suministrada podrá ser almacenada de forma segura en archivos o bases de datos del Distrito de Medellín para el cumplimiento de fines netamente institucionales. Tambien, podrá ser transmitida a terceros encargados del tratamiento o aliados, con quienes se tenga relación contractual o legal para cumplir con las finalidades aquí descritas y las obligaciones legales del Distrito. En esa medida declaro que esta información es correcta, veraz, verificable y actualizada y podrá ser sometida a '
  },
  {
    text: 'operaciones de recolección, almacenamiento, uso, circulación, indexación y/o analítica dentro del marco legal aplicable.\n',
    bold: true
  },
  {
    text: 'He sido informado sobre mi derecho a conocer, consultar, actualizar, rectificar y suprimir mi información; acceder a ella de forma gratuita; solicitar prueba de esta autorización y revocarla; presentar quejas ante la Superintendencia de Industria y Comercio (SIC), los que puedo ejercer gratuitamente a través de los canales de atención habilitados para el efecto, relacionados en la Política de Tratamiento de Datos Personales (Decreto 0268 de 2025), disponible en el portal web www.medellin.gov.co.\n'
  },
  {
    text: 'MI AUTORIZACIÓN SE PERFECCIONA',
    bold: true
  },
  {
    text: ' al momento de diligenciar y/o firmar el presente documento.'
  }
];

const FO086_PRIVACY_NOTICE_PARTS = [
  {
    text: 'AVISO DE PRIVACIDAD:',
    bold: true
  },
  {
    text: ' En cumplimiento de la Ley 1581 de 2012 y sus normas reglamentarias y complementarias el Distrito Especial de Ciencia, Tecnología e Innovación de Medellín, identificado con NIT 890.905.211-1 y con domicilio en el Centro Administrativo Distrital (CAD), Calle 44 N° 52–165, y contacto en la Línea Única (+57) 6044444144, como Responsable informa que podrá tratar sus datos personales conforme a su Política de Tratamiento de Datos Personales Decreto 0268 de 2025 disponible en www.medellin.gov.co, con las finalidades: Registro de asistencia a eventos, reuniones o actividades institucionales; para comunicaciones, notificaciones o requerimientos relacionados con trámites administrativos del Distrito de Medellín y además finalidades que de acuerdo con el artículo 10 de la Ley 1581 de 2012 no requieren autorización previa del Titular, por cuanto los datos a recopilar son necesarios para el cumplimiento de sus obligaciones legales, contractuales o del ejercicio de funciones públicas. El Distrito de Medellín recolectará datos sensibles y de niños, niñas y adolescentes los cuales puede negarse a suministrarlos, teniendo siempre presente el derecho a no responder este tipo de preguntas conforme a lo establecido en la Ley 1581 de 2012 y sus normas reglamentarias. El tratamiento de los datos de las niñas, niños y adolescentes se realizará en todo momento garantizando el interés superior y los derechos prevalentes de los mismos.\nLa información personal suministrada podrá ser almacenada de forma segura en archivos o bases de datos del Distrito de Medellín para el cumplimiento de fines netamente institucionales; en esa medida, se entiende que esta información es correcta, veraz, verificable y actualizada y podrá ser sometida a operaciones de recolección, almacenamiento, uso, circulación, indexación y/o analítica dentro del marco legal aplicable. Tambien, podrá ser transmitida a terceros encargados del tratamiento, con quienes el Distrito tenga relación contractual o legal, para cumplir con las finalidades aquí descritas y las obligaciones legales del Distrito.\nAdemás, se informa sobre el derecho de los titulares a conocer, consultar, actualizar, rectificar y suprimir su información; acceder a ella de forma gratuita; presentar quejas ante la Superintendencia de Industria y Comercio (SIC), los que puede ejercer gratuitamente a través de los canales de atención habilitados para el efecto, relacionados en la Política de Tratamiento de Datos Personales (Decreto 0268 de 2025), disponible en el portal web www.medellin.gov.co.'
  }
];

const CHARACTERIZATION_FORMAT_CONTENT = {
  'FO-GINF-093': {
    version: 'Versión 6',
    title: 'FO-GINF Listado de Asistencia con caracterización - Requiere autorización del Titular',
    privacyParts: FO093_PRIVACY_NOTICE_PARTS,
    organizer: 'Secretaría de Participación Ciudadana / Subsecretaría de Formación y Participación Ciudadana',
    projectName: 'Formación ciudadana',
    projectCode: '240187'
  },
  'FO-GINF-086': {
    version: 'Versión 1',
    title: 'FO-GINF Listado de asistencia con caracterización - No requiere autorización del Titular',
    privacyParts: FO086_PRIVACY_NOTICE_PARTS,
    organizer: 'Secretaría de Participación Ciudadana / Equipo de Control Social a la Gestión Pública',
    projectName: 'Control social a la gestión pública en el territorio',
    projectCode: '240186'
  }
};

function getCharacterizationFormatContent(formatCode) {
  return (
    CHARACTERIZATION_FORMAT_CONTENT[formatCode] ||
    CHARACTERIZATION_FORMAT_CONTENT['FO-GINF-093']
  );
}

function pickValue(source, ...keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '';
}

const FO086_ORGANIZATION_HEADER =
  '29. ORGANIZACIÓN, RED, GRUPO O SECTOR AL QUE PERTENECE (JAL, JAC, PP, PDL, OTROS, N/A)';

const FO086_INDICATOR_NAME =
  'Organizaciones, instancias e iniciativas ciudadanas, promovidas o fortalecidas en mecanismos de participación ciudadana, control social, gestión transparente, rendición social y pública de cuentas';
const FO086_INDICATOR_CODE = '3.2.1.2';

function getAttendanceIndicator(formatCode) {
  return formatCode === 'FO-GINF-086'
    ? { name: FO086_INDICATOR_NAME, code: FO086_INDICATOR_CODE }
    : { name: 'Grupos formados', code: '3.2.1.5' };
}

function isNadaDeNerviosMeeting(meeting) {
  const programScope = normalizeReportValue(
    meeting?.school?.programScope ??
    meeting?.school?.program_scope ??
    meeting?.group?.school?.programScope ??
    meeting?.group?.school?.program_scope
  );
  return programScope === 'ndn' || programScope.includes('nada de nervios');
}

function isDiplomaMeeting(meeting) {
  const programScope = normalizeReportValue(
    meeting?.school?.programScope ??
    meeting?.school?.program_scope ??
    meeting?.group?.school?.programScope ??
    meeting?.group?.school?.program_scope
  );
  return programScope === 'diplom';
}

export function getAttendanceCompany(beneficiary, formatCode, meeting) {
  if (formatCode === 'FO-GINF-086') {
    return pickValue(beneficiary, 'address');
  }

  if (
    formatCode === 'FO-GINF-093' &&
    (isNadaDeNerviosMeeting(meeting) || isDiplomaMeeting(meeting))
  ) {
    return 'N/A';
  }

  return pickValue(
    beneficiary,
    'organizationValue',
    'organization_value'
  );
}

export function getAttendanceRole(beneficiary, formatCode, meeting) {
  if (
    formatCode === 'FO-GINF-093' &&
    (isNadaDeNerviosMeeting(meeting) || isDiplomaMeeting(meeting))
  ) {
    return isReportAffirmative(
      pickValue(beneficiary, 'studyCurrently', 'study_currently')
    )
      ? 'Estudiante'
      : 'N/A';
  }

  return pickValue(beneficiary, 'occupationValue', 'occupation_value');
}

export function getAttendanceOrganization(beneficiary, meeting) {
  if (isDiplomaMeeting(meeting)) {
    return String(
      meeting?.group?.school?.name ||
      meeting?.school?.name ||
      ''
    );
  }

  if (isNadaDeNerviosMeeting(meeting)) {
    return String(
      meeting?.group?.school?.organization ||
      meeting?.school?.organization ||
      meeting?.group?.school?.name ||
      meeting?.school?.name ||
      meeting?.group?.school?.code ||
      meeting?.school?.code ||
      ''
    );
  }
  return pickValue(beneficiary, 'observations');
}

function getAttendanceOrganizationHeader(formatCode) {
  return formatCode === 'FO-GINF-086'
    ? FO086_ORGANIZATION_HEADER
    : '29. ORGANIZACIÓN, RED, GRUPO O SECTOR AL QUE PERTENECE';
}

function formatDateForReport(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';

  // meeting_date y birth_date son fechas de calendario. Se conserva el día
  // recibido del formulario sin convertirlo entre UTC y America/Bogota.
  const calendarDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (calendarDateMatch) {
    return `${calendarDateMatch[3]}/${calendarDateMatch[2]}/${calendarDateMatch[1]}`;
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;

  return parsed.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatTimeForReport(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';

  const timeMatch = normalized.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    const date = new Date(2020, 0, 1, Number(timeMatch[1]), Number(timeMatch[2]));
    return date.toLocaleTimeString('es-CO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function getBeneficiaryName(beneficiary) {
  return pickValue(beneficiary, 'fullName', 'full_name');
}

function getBeneficiaryDocumentType(beneficiary) {
  return pickValue(beneficiary, 'documentType', 'document_type') || 'CC';
}

function getBeneficiaryBirthDate(beneficiary) {
  return formatDateForReport(
    pickValue(beneficiary, 'birthDate', 'birth_date', 'birthdate')
  );
}

function getBeneficiaryEmail(beneficiary) {
  return pickValue(beneficiary, 'email', 'personalEmail', 'personal_email');
}

function getBeneficiaryPhone(beneficiary) {
  return pickValue(beneficiary, 'cellphone', 'cellPhone', 'cell_phone', 'phone');
}

function normalizeReportValue(value) {
  return String(value ?? '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function getBeneficiaryAge(beneficiary) {
  const storedAge = Number(pickValue(beneficiary, 'age'));
  if (Number.isFinite(storedAge) && storedAge >= 0) return storedAge;
  const value = pickValue(beneficiary, 'birthDate', 'birth_date');
  const birthDate = value ? new Date(value) : null;
  if (!birthDate || Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  if (
    today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
  ) age -= 1;
  return age;
}

function reportMark(condition) {
  return condition ? 'X' : '';
}

function isReportAffirmative(value) {
  if (value === true || value === 1) return true;

  const normalized = normalizeReportValue(value);
  return ['si', 'true', '1', 'x'].includes(normalized);
}

function getFO093AgeRangeMarks(beneficiary, age) {
  const selectedRange = normalizeReportValue(
    pickValue(beneficiary, 'gradeOrStudy', 'grade_or_study')
  );
  const selectedMarks = [
    selectedRange.includes('0-5'),
    selectedRange.includes('6-17'),
    selectedRange.includes('18-28'),
    selectedRange.includes('29-54'),
    selectedRange.includes('55')
  ];

  if (selectedMarks.some(Boolean)) {
    return selectedMarks;
  }

  return [
    age !== null && age <= 5,
    age !== null && age >= 6 && age <= 17,
    age !== null && age >= 18 && age <= 28,
    age !== null && age >= 29 && age <= 54,
    age !== null && age >= 55
  ];
}

function ExcelBorderCell({
  as: Element = 'td',
  borders = {},
  style,
  ...props
}) {
  const border = value => {
    if (!value) return 'none';
    return `${value === 'medium' ? 2 : 1}px solid #000`;
  };
  return (
    <Element
      {...props}
      style={{
        ...style,
        borderTop: border(borders.top),
        borderRight: border(borders.right),
        borderBottom: border(borders.bottom),
        borderLeft: border(borders.left)
      }}
    />
  );
}

function getFO093PopulationMarks(beneficiary = {}) {
  const gender = normalizeReportValue(
    pickValue(beneficiary, 'genderValue', 'gender_value', 'gender')
  );
  const ethnic = normalizeReportValue(pickValue(beneficiary, 'ethnicGroupValue', 'ethnic_group_value'));
  const orientation = normalizeReportValue(pickValue(beneficiary, 'sexualOrientationValue', 'sexual_orientation_value', 'lgbtiq'));
  const isLgbtiq =
    orientation.includes('lgtb') ||
    isReportAffirmative(pickValue(beneficiary, 'lgbtiq'));
  const disability = normalizeReportValue(pickValue(beneficiary, 'disabilityValue', 'disability_value', 'disability'));
  const victim = normalizeReportValue(pickValue(beneficiary, 'victimPopulationValue', 'victim_population_value'));
  const age = getBeneficiaryAge(beneficiary);
  const ageRangeMarks = getFO093AgeRangeMarks(beneficiary, age);
  return [
    gender.includes('hombre') || gender.includes('masculino'),
    gender.includes('mujer') || gender.includes('femenino'),
    ...ageRangeMarks,
    isAfroEthnicity(
      `${ethnic} ${pickValue(beneficiary, 'ethnicGroup', 'ethnic_group') || ''}`
    ),
    ethnic.includes('indigena'),
    ethnic.includes('rom'),
    isLgbtiq,
    normalizeReportValue(pickValue(beneficiary, 'riskFactorValue', 'risk_factor_value')).includes('calle'),
    isReportAffirmative(pickValue(beneficiary, 'farmer', 'isFarmer', 'is_farmer')),
    Boolean(disability && !disability.includes('no tiene') && !disability.includes('ninguna')),
    Boolean(victim && !victim.includes('no'))
  ];
}

export function FO093PrivacyNotice() {
  const paragraphs = [[]];
  FO093_PRIVACY_NOTICE_PARTS.forEach(part => {
    part.text.split('\n').forEach((text, index) => {
      if (index > 0) paragraphs.push([]);
      if (text) paragraphs[paragraphs.length - 1].push({ ...part, text });
    });
  });

  return (
    <>
      {paragraphs.map((parts, paragraphIndex) => (
        <p key={paragraphIndex}>
          {parts.map((part, partIndex) => part.bold
            ? <strong key={partIndex}>{part.text}</strong>
            : <span key={partIndex}>{part.text}</span>)}
        </p>
      ))}
    </>
  );
}

function FO086PrivacyNotice() {
  return (
    <>
      <p>
        <strong>AVISO DE PRIVACIDAD:</strong> En cumplimiento de la Ley 1581 de 2012 y sus normas reglamentarias y complementarias el Distrito Especial de Ciencia, Tecnología e Innovación de Medellín, identificado con NIT 890.905.211-1 y con domicilio en el Centro Administrativo Distrital (CAD), Calle 44 N° 52–165, y contacto en la Línea Única (+57) 6044444144, como Responsable informa que podrá tratar sus datos personales conforme a su Política de Tratamiento de Datos Personales Decreto 0268 de 2025 disponible en www.medellin.gov.co, con las finalidades: Registro de asistencia a eventos, reuniones o actividades institucionales; para comunicaciones, notificaciones o requerimientos relacionados con trámites administrativos del Distrito de Medellín y además finalidades que de acuerdo con el artículo 10 de la Ley 1581 de 2012 no requieren autorización previa del Titular, por cuanto los datos a recopilar son necesarios para el cumplimiento de sus obligaciones legales, contractuales o del ejercicio de funciones públicas. El Distrito de Medellín recolectará datos sensibles y de niños, niñas y adolescentes los cuales puede negarse a suministrarlos, teniendo siempre presente el derecho a no responder este tipo de preguntas conforme a lo establecido en la Ley 1581 de 2012 y sus normas reglamentarias. El tratamiento de los datos de las niñas, niños y adolescentes se realizará en todo momento garantizando el interés superior y los derechos prevalentes de los mismos.
      </p>
      <p>
        La información personal suministrada podrá ser almacenada de forma segura en archivos o bases de datos del Distrito de Medellín para el cumplimiento de fines netamente institucionales; en esa medida, se entiende que esta información es correcta, veraz, verificable y actualizada y podrá ser sometida a operaciones de recolección, almacenamiento, uso, circulación, indexación y/o analítica dentro del marco legal aplicable. Tambien, podrá ser transmitida a terceros encargados del tratamiento, con quienes el Distrito tenga relación contractual o legal, para cumplir con las finalidades aquí descritas y las obligaciones legales del Distrito.
      </p>
      <p>
        Además, se informa sobre el derecho de los titulares a conocer, consultar, actualizar, rectificar y suprimir su información; acceder a ella de forma gratuita; presentar quejas ante la Superintendencia de Industria y Comercio (SIC), los que puede ejercer gratuitamente a través de los canales de atención habilitados para el efecto, relacionados en la Política de Tratamiento de Datos Personales (Decreto 0268 de 2025), disponible en el portal web www.medellin.gov.co.
      </p>
    </>
  );
}

export function getMeetingTopic(meeting) {
  const topic = formatMeetingTopic(meeting);
  return topic || getDefaultMeetingTopic(meeting);
}

function getDefaultMeetingTopic(meeting) {
  const isCopMeeting = String(meeting?.type ?? meeting?.meeting_type ?? '')
    .trim()
    .toUpperCase()
    .startsWith('COP_');
  if (isCopMeeting) {
    return String(
      meeting?.school?.name ||
      meeting?.school?.organization ||
      meeting?.school?.code ||
      'Asesoría'
    ).trim();
  }

  const activityGroup = meeting?.activityGroup ?? meeting?.activity_group;
  if (activityGroup) {
    const activityName = String(
      activityGroup?.activity?.name ??
      meeting?.activity?.name ??
      ''
    ).trim();
    const activityCode = String(activityGroup?.code || '').trim();

    if (activityName && activityCode) return `${activityName} - ${activityCode}`;
    return activityName || activityCode || 'Control Social';
  }

  const meetingOrder = meeting?.order ?? meeting?.order_number;
  const groupCode =
    meeting?.group?.code ??
    meeting?.activityGroup?.code ??
    meeting?.activity_group?.code ??
    meeting?.group_code ??
    meeting?.groupCode;

  if (meetingOrder !== undefined && meetingOrder !== null && groupCode) {
    return `Encuentro #${meetingOrder} - ${String(groupCode).trim()}`;
  }

  return (
    pickValue(meeting, 'goal', 'name', 'title') ||
    `Encuentro #${meetingOrder ?? '---'} - ${groupCode || '---'}`
  );
}

function getMeetingResponsible(meeting, meetingGroup, meetingSchool) {
  return (
    meetingGroup.user?.full_name ||
    meeting.user?.full_name ||
    meetingSchool.user?.full_name ||
    'Coordinador'
  );
}

function getMeetingPlace(meeting, meetingGroup, meetingSchool) {
  if (String(meeting?.type ?? meeting?.meeting_type ?? '').trim().toUpperCase().startsWith('COP_')) {
    return '';
  }

  return (
    pickValue(meeting, 'place', 'location') ||
    meetingGroup.place ||
    meetingSchool.name ||
    meetingSchool.organization ||
    meetingSchool.code ||
    meeting?.activityGroup?.activity?.name ||
    meeting?.activity_group?.activity?.name ||
    meetingGroup.school?.name ||
    '---'
  );
}

function getMeetingNeighborhood(meeting, meetingSchool) {
  return (
    pickValue(meetingSchool?.neighborhood, 'name') ||
    pickValue(meeting?.school?.neighborhood, 'name') ||
    ''
  );
}

function getMeetingAddress(meeting, meetingSchool) {
  if (String(meeting?.type ?? meeting?.meeting_type ?? '').trim().toUpperCase().startsWith('COP_')) {
    return '';
  }
  return pickValue(meeting, 'address') || pickValue(meetingSchool, 'address');
}

function getBeneficiaryCommuneNumber(beneficiary = {}) {
  const communeValue = String(
    beneficiary?.municipality?.name ||
    pickValue(beneficiary, 'municipalityValue', 'municipality_value') ||
    ''
  ).trim();
  const match = communeValue.match(/(?:comuna\s*)?(\d{1,2})/i);
  return match ? String(Number(match[1])) : '';
}

function chunkRecords(records, size) {
  if (!records.length) return [[]];

  const chunks = [];
  for (let index = 0; index < records.length; index += size) {
    chunks.push(records.slice(index, index + size));
  }
  return chunks;
}

function buildReportFileName(meeting) {
  const topic = getDefaultMeetingTopic(meeting)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .trim();
  return `${topic || 'Listado de asistencia'}.pdf`;
}

function buildAttendanceExcelFileName(meeting) {
  const topic = getDefaultMeetingTopic(meeting)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .trim();
  return `${topic || 'Listado de asistencia'}.xlsx`;
}

const REPORT_MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function getAttendanceReportMonth(activityGroup, meetingDate) {
  const configuredMonth = Number(
    activityGroup?.month ??
    activityGroup?.reportMonth ??
    activityGroup?.report_month
  );
  if (Number.isInteger(configuredMonth) && configuredMonth >= 1 && configuredMonth <= 12) {
    return REPORT_MONTH_NAMES[configuredMonth - 1];
  }

  const dateValue = String(
    meetingDate ?? activityGroup?.createdAt ?? activityGroup?.created_at ?? ''
  ).trim();
  const calendarMonth = dateValue.match(/^\d{4}-(\d{2})-/);
  if (calendarMonth) return REPORT_MONTH_NAMES[Number(calendarMonth[1]) - 1] || '';

  const parsedDate = dateValue ? new Date(dateValue) : null;
  return parsedDate && !Number.isNaN(parsedDate.getTime())
    ? REPORT_MONTH_NAMES[parsedDate.getMonth()] || ''
    : '';
}

function generateCharacterizationExcel({
  meeting,
  attendedRecords,
  formatCode
}) {
  const activityGroup = meeting?.activityGroup ?? meeting?.activity_group ?? {};
  const activity = activityGroup?.activity ?? {};
  const meetingDate = meeting?.meeting_date ?? meeting?.meetingDate;
  const organizationHeader = getAttendanceOrganizationHeader(formatCode)
    .replace(/^\d+\.\s*/, '');
  const headers = [
    'No.',
    'TD',
    'NÚMERO DE DOCUMENTO',
    'NOMBRES Y APELLIDOS',
    'EMPRESA / ENTIDAD / DEPENDENCIA',
    'CARGO / EMPLEO / CALIDAD EN LA QUE ACTÚA',
    'CORREO ELECTRÓNICO',
    'NÚMERO DE CELULAR',
    'HOMBRE',
    'MUJER',
    'Sin Dato',
    'PRIMERA INFANCIA 0-5',
    'NIÑEZ-ADOLESCENCIA 6-17',
    'JUVENTUD 18-28',
    'ADULTO/A 29-54',
    'ADULTO MAYOR 55 O MÁS',
    'Sin Dato',
    'AFRODESCENDIENTE',
    'INDÍGENA',
    'ROM',
    'LGTBI',
    'SITUACIÓN DE CALLE',
    'CAMPESINO',
    'DISCAPACIDAD',
    'POBLACIÓN VÍCTIMA',
    '# COMUNA DONDE RESIDE',
    organizationHeader,
    'Actividad en la que participó',
    'Estrategia',
    'Fecha',
    'Mes del reporte'
  ];

  const rows = attendedRecords.map((record, index) => {
    const beneficiary = record?.beneficiary || {};
    const populationMarks = getFO093PopulationMarks(beneficiary);
    const genderMarks = populationMarks.slice(0, 2);
    const ageMarks = populationMarks.slice(2, 7);
    const differentialMarks = populationMarks.slice(7);
    return [
      index + 1,
      getBeneficiaryDocumentType(beneficiary),
      pickValue(beneficiary, 'document'),
      getBeneficiaryName(beneficiary),
      getAttendanceCompany(beneficiary, formatCode, meeting),
      getAttendanceRole(beneficiary, formatCode, meeting),
      getBeneficiaryEmail(beneficiary),
      getBeneficiaryPhone(beneficiary),
      ...genderMarks.map(reportMark),
      reportMark(!genderMarks.some(Boolean)),
      ...ageMarks.map(reportMark),
      reportMark(!ageMarks.some(Boolean)),
      ...differentialMarks.map(reportMark),
      getBeneficiaryCommuneNumber(beneficiary),
      getAttendanceOrganization(beneficiary, meeting),
      activity?.name ?? '',
      activity?.line ?? '',
      formatDateForReport(meetingDate),
      getAttendanceReportMonth(activityGroup, meetingDate)
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  worksheet['!cols'] = headers.map((header, index) => ({
    wch: index === 0 ? 8 : Math.min(Math.max(header.length + 2, 14), 42)
  }));
  worksheet['!rows'] = [{ hpt: 54 }];
  const lastColumn = XLSX.utils.encode_col(headers.length - 1);
  worksheet['!autofilter'] = {
    ref: `A1:${lastColumn}${Math.max(rows.length + 1, 1)}`
  };
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2' };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Listado de asistencia');
  XLSX.writeFile(workbook, buildAttendanceExcelFileName(meeting));
}

function readBlobAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadImageDataUrl(sourceUrl, useAuth = false) {
  if (!sourceUrl) return '';
  if (/^data:/i.test(sourceUrl)) return sourceUrl;

  try {
    const response = useAuth
      ? await fetchWithAuth(sourceUrl)
      : await fetch(sourceUrl, { credentials: 'include' });
    if (!response.ok) throw new Error(`IMAGE_${response.status}`);
    return readBlobAsDataUrl(await response.blob());
  } catch (error) {
    console.error('Error cargando imagen para PDF', error);
    return '';
  }
}

function getDataUrlImageFormat(dataUrl) {
  const normalized = String(dataUrl || '').toLowerCase();
  if (normalized.startsWith('data:image/png')) return 'PNG';
  return 'JPEG';
}

function getPdfColumnStyles(contentWidth) {
  const totalWidth = FO083_COLUMN_WIDTHS.reduce((sum, width) => sum + width, 0);

  return FO083_COLUMN_WIDTHS.reduce((styles, width, index) => {
    styles[index] = {
      cellWidth: (width / totalWidth) * contentWidth
    };
    return styles;
  }, {});
}

function pdfCell(content = '', options = {}) {
  const { styles = {}, ...rest } = options;
  return {
    content: content ?? '',
    ...rest,
    styles
  };
}

function withPdfRowHeight(row, height) {
  return row.map((cell) => ({
    ...cell,
    styles: {
      ...(cell.styles || {}),
      minCellHeight: height
    }
  }));
}

export function drawImageInCell(doc, dataUrl, cell, maxWidth, maxHeight) {
  if (!dataUrl) return;

  try {
    const image = doc.getImageProperties(dataUrl);
    const scale = Math.min(
      Math.min(maxWidth, cell.width - 4) / image.width,
      Math.min(maxHeight, cell.height - 4) / image.height
    );
    if (!Number.isFinite(scale) || scale <= 0) return;
    const width = image.width * scale;
    const height = image.height * scale;
    const x = cell.x + (cell.width - width) / 2;
    const y = cell.y + (cell.height - height) / 2;
    doc.addImage(
      dataUrl,
      getDataUrlImageFormat(dataUrl),
      x,
      y,
      width,
      height,
      undefined,
      'FAST'
    );
  } catch (error) {
    console.error('Error dibujando imagen en PDF', error);
  }
}

function getPdfTextWidth(doc, text, bold, fontSize) {
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(fontSize);
  return doc.getTextWidth(text);
}

function drawRichText(doc, parts, x, y, maxWidth, fontSize, lineHeight, boxHeight) {
  const lines = [];
  let currentLine = [];
  let currentWidth = 0;

  function flushLine() {
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }
    currentLine = [];
    currentWidth = 0;
  }

  function appendToken(token, bold) {
    if (!token) return;

    const normalizedToken = /^\s+$/.test(token) ? ' ' : token;
    if (normalizedToken === ' ' && currentLine.length === 0) return;

    const tokenWidth = getPdfTextWidth(doc, normalizedToken, bold, fontSize);
    if (currentLine.length > 0 && currentWidth + tokenWidth > maxWidth) {
      flushLine();
      if (normalizedToken === ' ') return;
    }

    currentLine.push({ text: normalizedToken, bold });
    currentWidth += tokenWidth;
  }

  parts.forEach((part) => {
    const textLines = String(part.text || '').split('\n');
    textLines.forEach((lineText, lineIndex) => {
      if (lineIndex > 0) flushLine();
      lineText.split(/(\s+)/).forEach((token) => appendToken(token, part.bold));
    });
  });
  flushLine();

  const textHeight = fontSize + Math.max(0, lines.length - 1) * lineHeight;
  let cursorY = boxHeight === undefined
    ? y
    : y + (boxHeight - textHeight) / 2 + fontSize * 0.8;
  doc.setTextColor(0, 0, 0);
  lines.forEach((line) => {
    let cursorX = x;
    line.forEach((segment) => {
      doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.text(segment.text, cursorX, cursorY);
      cursorX += doc.getTextWidth(segment.text);
    });
    cursorY += lineHeight;
  });
}

function getAttendanceSignatureId(attendance) {
  return attendance?.image?.id || null;
}

function isSignedAttendance(attendance) {
  return (
    (attendance?.attended === true || attendance?.attended === 1) &&
    Boolean(getAttendanceSignatureId(attendance))
  );
}

async function loadSignatureDataUrls(records) {
  const signatureIds = Array.from(
    new Set(records.map(getAttendanceSignatureId).filter(Boolean))
  );

  const entries = await Promise.all(
    signatureIds.map(async (imageId) => {
      const imageUrl = buildProtectedImageUrl(imageId, false);
      const dataUrl = await loadImageDataUrl(imageUrl, true);
      return [imageId, dataUrl];
    })
  );

  return new Map(entries.filter(([, dataUrl]) => Boolean(dataUrl)));
}

function buildPdfRows({
  pageIndex,
  records,
  meeting,
  meetingGroup,
  meetingSchool,
  signatureDataUrls,
  formatCode
}) {
  const rowSlots = Array.from({ length: ROWS_PER_SHEET }, (_, index) => ({
    record: records[index] || null,
    rowNumber: pageIndex * ROWS_PER_SHEET + index + 1
  }));
  const meetingDate = formatDateForReport(meeting.meeting_date || meeting.meetingDate);
  const meetingStartTime =
    formatTimeForReport(pickValue(meeting, 'startTime', 'start_time')) || '';
  const meetingEndTime =
    formatTimeForReport(pickValue(meeting, 'endTime', 'end_time')) || '';

  const rows = [
    withPdfRowHeight([
      pdfCell(`Cód. ${formatCode}`, {
        colSpan: 8,
        styles: { fontStyle: 'bold', halign: 'center' }
      }),
      pdfCell(
        'Formato\nFO-GINF Asistencia a reuniones, eventos y capacitaciones de niñas, niños y adolescentes con autorización previa del tratamiento por parte de sus Representantes Legales',
        {
          colSpan: 24,
          rowSpan: 2,
          styles: {
            fontSize: 6.5,
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle'
          }
        }
      ),
      pdfCell('', {
        colSpan: 6,
        rowSpan: 2,
        imageType: 'logo',
        styles: { halign: 'center', valign: 'middle' }
      })
    ], 15),
    withPdfRowHeight([
      pdfCell('Versión 2', {
        colSpan: 8,
        styles: { fontStyle: 'bold', halign: 'center' }
      })
    ], 17),
    withPdfRowHeight([
      pdfCell('Tema', { colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(getMeetingTopic(meeting), { colSpan: 24, styles: { halign: 'left' } }),
      pdfCell('Tipo de Reunión', {
        colSpan: 3,
        rowSpan: 3,
        styles: { fontStyle: 'bold', halign: 'center', valign: 'middle' }
      }),
      pdfCell('Capacitación', { colSpan: 5, styles: { halign: 'left' } }),
      pdfCell('X', { colSpan: 2, styles: { fontStyle: 'bold', halign: 'center' } })
    ], 22),
    withPdfRowHeight([
      pdfCell('Dependencia', { colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(
        'Secretaría de Participación Ciudadana/ Subsecretaría de Formación y Participación Ciudadana/ Unidad de Gestión Formativa',
        { colSpan: 24, styles: { halign: 'left' } }
      ),
      pdfCell('Reunion', { colSpan: 5, styles: { halign: 'left' } }),
      pdfCell('', { colSpan: 2 })
    ], 22),
    withPdfRowHeight([
      pdfCell('Responsable', { colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(getMeetingResponsible(meeting, meetingGroup, meetingSchool), {
        colSpan: 24,
        styles: { halign: 'left' }
      }),
      pdfCell('Evento', { colSpan: 5, styles: { halign: 'left' } }),
      pdfCell('', { colSpan: 2 })
    ], 22),
    withPdfRowHeight([
      pdfCell('Fecha', { colSpan: 4, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(meetingDate, { colSpan: 4, styles: { halign: 'left' } }),
      pdfCell('Lugar', { colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(getMeetingPlace(meeting, meetingGroup, meetingSchool), {
        colSpan: 10,
        styles: { halign: 'left' }
      }),
      pdfCell('Hora inicia', { colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(meetingStartTime, { colSpan: 4, styles: { halign: 'left' } }),
      pdfCell('Hora termina', { colSpan: 3, styles: { fontStyle: 'bold', halign: 'left' } }),
      pdfCell(meetingEndTime, { colSpan: 7, styles: { halign: 'left' } })
    ], 22),
    withPdfRowHeight([
      pdfCell('', {
        colSpan: 38,
        privacy: true,
        styles: { cellPadding: 0, overflow: 'hidden' }
      })
    ], 104),
    withPdfRowHeight([
      pdfCell('N°', { styles: { fontStyle: 'bold' } }),
      pdfCell('Tipo\nDcto', { styles: { fontStyle: 'bold' } }),
      pdfCell('Número de Documento', { colSpan: 3, styles: { fontStyle: 'bold' } }),
      pdfCell('Nombres y Apellidos', { colSpan: 10, styles: { fontStyle: 'bold' } }),
      pdfCell('Fecha Nacimiento\n(dd/mm/aaaa)', { colSpan: 4, styles: { fontStyle: 'bold' } }),
      pdfCell('Teléfono Fijo / Número Celular', { colSpan: 3, styles: { fontStyle: 'bold' } }),
      pdfCell('Correo Electrónico', { colSpan: 10, styles: { fontStyle: 'bold' } }),
      pdfCell('Firma', { colSpan: 6, styles: { fontStyle: 'bold' } })
    ], 28)
  ];

  rowSlots.forEach(({ record, rowNumber }) => {
    const beneficiary = record?.beneficiary || {};
    const signatureId = record ? getAttendanceSignatureId(record) : null;
    rows.push(withPdfRowHeight([
      pdfCell(rowNumber, { styles: { overflow: 'ellipsize' } }),
      pdfCell(record ? getBeneficiaryDocumentType(beneficiary) : '', {
        styles: { overflow: 'ellipsize' }
      }),
      pdfCell(record ? pickValue(beneficiary, 'document') : '', {
        colSpan: 3,
        styles: { overflow: 'ellipsize' }
      }),
      pdfCell(record ? getBeneficiaryName(beneficiary) : '', {
        colSpan: 10,
        styles: { fontStyle: 'bold', overflow: 'ellipsize' }
      }),
      pdfCell(record ? getBeneficiaryBirthDate(beneficiary) : '', {
        colSpan: 4,
        styles: { overflow: 'ellipsize' }
      }),
      pdfCell(record ? getBeneficiaryPhone(beneficiary) : '', {
        colSpan: 3,
        styles: { overflow: 'ellipsize' }
      }),
      pdfCell(record ? getBeneficiaryEmail(beneficiary) : '', {
        colSpan: 10,
        styles: { overflow: 'ellipsize' }
      }),
      pdfCell('', {
        colSpan: 6,
        signatureDataUrl: signatureId ? signatureDataUrls.get(signatureId) : '',
        styles: { cellPadding: 0 }
      })
    ], 20));
  });

  return rows;
}

async function generateAttendancePdf({
  meeting,
  meetingGroup,
  meetingSchool,
  sheets,
  attendedRecords,
  formatCode = 'FO-GINF-083',
  download = true
}) {
  const doc = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter',
    compress: true
  });
  const margin = 18;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  const logoDataUrl = await loadImageDataUrl(medellinLogo);
  const signatureDataUrls = await loadSignatureDataUrls(attendedRecords);
  const columnStyles = getPdfColumnStyles(contentWidth);

  sheets.forEach((records, pageIndex) => {
    if (pageIndex > 0) {
      doc.addPage('letter', 'landscape');
    }

    doc.autoTable({
      startY: margin,
      body: buildPdfRows({
        pageIndex,
        records,
        meeting,
        meetingGroup,
        meetingSchool,
        signatureDataUrls,
        formatCode
      }),
      columnStyles,
      margin: {
        top: margin,
        right: margin,
        bottom: margin,
        left: margin
      },
      tableWidth: contentWidth,
      theme: 'plain',
      styles: {
        cellPadding: 1.2,
        font: 'helvetica',
        fontSize: 6.4,
        halign: 'center',
        lineColor: [0, 0, 0],
        lineWidth: 0.35,
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        valign: 'middle'
      },
      didDrawCell: (data) => {
        const raw = data.cell.raw;
        if (!raw || typeof raw !== 'object') return;

        if (raw.imageType === 'logo') {
          drawImageInCell(doc, logoDataUrl, data.cell, 68, 30);
        }

        if (raw.signatureDataUrl) {
          drawImageInCell(doc, raw.signatureDataUrl, data.cell, 86, 16);
        }

        if (raw.privacy) {
          drawRichText(
            doc,
            NNA_PRIVACY_NOTICE_PARTS,
            data.cell.x + 4,
            data.cell.y,
            data.cell.width - 8,
            5.1,
            6.1,
            data.cell.height
          );
        }
      }
    });

    if (sheets.length > 1) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.text(`Página ${pageIndex + 1} de ${sheets.length}`, pageWidth - margin - 48, pageHeight - 8);
    }
  });

  if (download) {
    doc.save(buildReportFileName(meeting));
    return null;
  }

  return doc.output('blob');
}

async function generateFO093Pdf({
  meeting,
  meetingGroup,
  meetingSchool,
  sheets,
  attendedRecords,
  formatCode = 'FO-GINF-093',
  download = true,
  showTotals = true
}) {
  const formatContent = getCharacterizationFormatContent(formatCode);
  const indicator = getAttendanceIndicator(formatCode);
  const doc = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'legal',
    compress: true
  });
  const logoDataUrl = await loadImageDataUrl(medellinLogo);
  const signatureDataUrls = await loadSignatureDataUrls(attendedRecords);
  const thinBorder = 0.35;
  const mediumBorder = 0.8;
  const blueFill = [221, 235, 247];
  const grayFill = [217, 217, 217];
  const whiteFill = [255, 255, 255];
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 18;
  const tableWidth = pageWidth - margin * 2;
  const excelWidthTotal = FO093_HEADER_COLUMN_WIDTHS.reduce((total, width) => total + width, 0);
  const columnStyles = Object.fromEntries(
    FO093_HEADER_COLUMN_WIDTHS.map((width, index) => [
      index,
      { cellWidth: (width / excelWidthTotal) * tableWidth }
    ])
  );
  const borderWidth = (value) => {
    if (value === 'medium') return mediumBorder;
    if (value === 'thin') return thinBorder;
    return 0;
  };
  const pdfCell = (
    content,
    {
      borders = {},
      fillColor = whiteFill,
      colSpan,
      rowSpan,
      styles = {},
      ...metadata
    } = {}
  ) => ({
    content,
    ...metadata,
    ...(colSpan ? { colSpan } : {}),
    ...(rowSpan ? { rowSpan } : {}),
    styles: {
      fillColor,
      lineColor: [0, 0, 0],
      lineWidth: {
        top: borderWidth(borders.top),
        right: borderWidth(borders.right),
        bottom: borderWidth(borders.bottom),
        left: borderWidth(borders.left)
      },
      ...styles
    }
  });
  const blueCell = (content, options = {}) =>
    pdfCell(content, { ...options, fillColor: blueFill });
  const grayCell = (content, options = {}) =>
    pdfCell(content, { ...options, fillColor: grayFill });
  const thinBorders = {
    top: 'thin',
    right: 'thin',
    bottom: 'thin',
    left: 'thin'
  };
  const borderedCell = (content, options = {}) =>
    pdfCell(content, {
      ...options,
      borders: { ...thinBorders, ...(options.borders || {}) }
    });
  const blueBorderedCell = (content, options = {}) =>
    borderedCell(content, { ...options, fillColor: blueFill });
  const withRowHeight = (row, minCellHeight) =>
    row.map(cell => ({
      ...cell,
      styles: {
        ...(cell.styles || {}),
        minCellHeight
      }
    }));
  const detailColumnWidths = [
    8.99, 20.22, 7.41, 4.9, 4.9, 4.96, 4.85, 3.22,
    2.23, 6.1, 6.05, 5.83, 7.25, 5.23, 7.85
  ];
  const detailWidthTotal = detailColumnWidths.reduce((total, width) => total + width, 0);
  const pdfMargins = {
    top: margin,
    right: margin,
    bottom: margin,
    left: margin
  };
  const detailColumnStyles = Object.fromEntries(
    detailColumnWidths.map((width, index) => [
      index,
      { cellWidth: (width / detailWidthTotal) * tableWidth }
    ])
  );
  const verticalHeaderLabels = {
    0: 'CONSECUTIVO',
    1: 'Tipo de Documento',
    6: 'Hombre',
    7: 'Mujer',
    8: 'Primera infancia 0-5',
    9: 'Niñez-Adolescencia 6-17',
    10: 'Juventud 18-28',
    11: 'Adulto/a. 29-54',
    12: 'Adulto Mayor 55 o más',
    13: 'Afrodescendiente',
    14: 'Indígena',
    15: 'ROM',
    16: 'LGTBI',
    17: 'Situación de calle',
    18: 'Campesino',
    19: 'Discapacidad',
    20: 'Población víctima',
    21: '27. # COMUNA DONDE RESIDE'
  };
  const verticalBorders = [
    { top: 'medium', left: 'medium', right: 'thin', bottom: 'medium' },
    { top: 'medium', left: 'thin', right: 'medium', bottom: 'thin' },
    { top: 'medium', left: 'medium', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'medium', bottom: 'thin' },
    { top: 'medium', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'medium' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'thin', bottom: 'thin' },
    { top: 'medium', left: 'medium', right: 'medium', bottom: 'thin' }
  ];
  const headerRows = [
    [
      blueCell('', { borders: { top: 'medium', left: 'medium', bottom: 'medium' }, styles: { minCellHeight: 10 } }),
      blueCell('', { borders: { top: 'medium', bottom: 'medium' } }),
      blueCell('IDENTIFICACIÓN', { colSpan: 4, borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' } }),
      grayCell('DESAGREGACIÓN POBLACIONAL', { colSpan: 15, borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' } }),
      grayCell('', { borders: { top: 'medium', left: 'medium', right: 'medium' } }),
      grayCell('DESAGREGACIÓN TERRITORIAL', { colSpan: 3, borders: { top: 'medium', right: 'medium', bottom: 'medium' } }),
      pdfCell('', { borders: { top: 'medium', left: 'medium', bottom: 'medium' } }),
      pdfCell('', { borders: { top: 'medium', right: 'thin', bottom: 'medium' } }),
      pdfCell('', { borders: { top: 'medium', left: 'thin', right: 'medium', bottom: 'medium' } })
    ],
    [
      blueCell('', { borders: { top: 'medium', left: 'medium', right: 'medium' }, styles: { minCellHeight: 22 } }),
      blueCell('', {
        arrow: true,
        borders: { top: 'medium', left: 'medium' }
      }),
      blueCell('19. TIPO DE DOCUMENTO (TD) (detalle)', { colSpan: 4, borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' } }),
      grayCell('24. SEXO', {
        colSpan: 2,
        borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' },
        styles: { halign: 'left' }
      }),
      grayCell('25. RANGO EDAD', {
        colSpan: 5,
        borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' },
        styles: { halign: 'left' }
      }),
      grayCell('26. ENFOQUE DIFERENCIAL*', {
        colSpan: 7,
        borders: { top: 'medium', bottom: 'medium' },
        styles: { halign: 'left' }
      }),
      grayCell('DERE\nCHOS*', { borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }, styles: { halign: 'left' } }),
      grayCell('', {
        arrow: true,
        borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }
      }),
      grayCell('27. COMUNAS Y CORREGIMIENTOS (DETALLE)', {
        colSpan: 3,
        borders: { top: 'medium', right: 'medium', bottom: 'medium' },
        styles: { halign: 'left' }
      }),
      grayCell('16. NOMBRE(S) DE INDICADOR(ES) DE PLAN DE DLLO (PDM) / PLAN INDICATIVO (PI) - (Diligencia el organizador)', {
        colSpan: 2,
        borders: { right: 'medium', bottom: 'medium' }
      }),
      grayCell('17. CÓDIGO(S) DE INDICADOR(ES) DE PDM Y PI', {
        borders: { left: 'medium', right: 'medium', bottom: 'medium' }
      })
    ],
    [
      blueCell('', {
        verticalFontSize: 3.1,
        verticalLabel: verticalHeaderLabels[0],
        borders: { left: 'medium', right: 'medium' },
        styles: { minCellHeight: 44 }
      }),
      blueCell('', {
        verticalFontSize: 3.1,
        verticalLabel: verticalHeaderLabels[1],
        borders: { left: 'medium', right: 'medium' }
      }),
      blueCell('\nCC    = Cédula de ciudadanía\nTI      =  Tarjeta de identidad\nCE    = Cédula de extranjería', {
        colSpan: 2,
        borders: { top: 'medium', left: 'medium', bottom: 'medium' },
        styles: { fontSize: 5.2, halign: 'left' }
      }),
      blueCell('RC    = Registro civil\nNUIP = Número Único de Identificación Personal\nNIP   = Número de Identificación Personal', {
        colSpan: 2,
        borders: { top: 'medium', right: 'medium', bottom: 'medium' },
        styles: { fontSize: 5.2, halign: 'left' }
      }),
      ...verticalBorders.map((borders, index) =>
        blueCell('', {
          rowSpan: 2,
          verticalFontSize: 4.6,
          verticalLabel: verticalHeaderLabels[index + 6],
          borders: { ...borders, bottom: 'medium' }
        })
      ),
      blueCell('', {
        rowSpan: 2,
        verticalFontSize: 3,
        verticalLabel: verticalHeaderLabels[21],
        borders: { left: 'medium', right: 'medium', bottom: 'medium' }
      }),
      grayCell('1. Popular\n2. Santa Cruz\n3. Manrique\n4. Aranjuez\n5. Castilla\n6.12Octubre\n7.Robledo\n8. Villahermosa', {
        styles: { fontStyle: 'normal', fontSize: 3.3, halign: 'left', valign: 'top' }
      }),
      grayCell('9. Buenos Aires\n10.La Candelaria\n11. Laureles Estadio\n12. La América\n13. San Javier\n14. El Poblado\n15. Guayabal\n16. Belén', {
        styles: { fontStyle: 'normal', fontSize: 3.3, halign: 'left', valign: 'top' }
      }),
      grayCell('50.San Sebastián de Palmitas\n60. San Cristóbal\n70. AltaVista\n80. San Ant. de Prado\n90. Santa Elena.', {
        borders: { right: 'medium' },
        styles: { fontStyle: 'normal', fontSize: 3.3, halign: 'left' }
      }),
      pdfCell(indicator.name, {
        colSpan: 2,
        borders: { top: 'thin', left: 'medium', right: 'thin', bottom: 'medium' },
        styles: { fontStyle: 'normal' }
      }),
      pdfCell(indicator.code, {
        borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' },
        styles: { fontStyle: 'normal' }
      })
    ],
    [
      blueCell('18. No.', { borders: { top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }, styles: { minCellHeight: 26 } }),
      blueCell('19. TD', { borders: { top: 'medium', left: 'thin', right: 'medium', bottom: 'medium' } }),
      blueCell('20. NÚMERO DE DOCUMENTO', { borders: { right: 'thin', bottom: 'medium' } }),
      blueCell('21. NOMBRES Y APELLIDOS', { borders: { left: 'thin', right: 'medium', bottom: 'medium' } }),
      blueCell('22. EMPRESA / ENTIDAD / DEPENDENCIA', { borders: { left: 'thin', right: 'medium', bottom: 'medium' } }),
      blueCell('23. CARGO / EMPLEO / CALIDAD EN LA QUE ACTÚA', { borders: { left: 'thin', right: 'medium', bottom: 'medium' } }),
      blueCell('28. CORREO ELECTRÓNICO', { colSpan: 3, borders: { top: 'medium', right: 'medium', bottom: 'medium' }, styles: { fontSize: 5 } }),
      blueCell(getAttendanceOrganizationHeader(formatCode), { borders: { right: 'thin', bottom: 'medium' } }),
      blueCell('30. TELÉFONO FIJO / NÚMERO DE CELULAR', { borders: { right: 'medium', bottom: 'medium' } }),
      blueCell('31. FIRMA', { borders: { left: 'medium', right: 'medium', bottom: 'medium' }, styles: { fontSize: 5 } })
    ]
  ];

  sheets.forEach((records, pageIndex) => {
    if (pageIndex) doc.addPage('legal', 'landscape');

    const date = formatDateForReport(
      meeting.meeting_date || meeting.meetingDate
    ).split('/');
    const brandRows = [
      withRowHeight([
        borderedCell(`Cód. ${formatCode}`, {
          colSpan: 3,
          styles: { fontSize: 5.3, fontStyle: 'bold' }
        }),
        borderedCell('Formato', {
          colSpan: 24,
          styles: { fontSize: 7.5, fontStyle: 'normal' }
        }),
        borderedCell('', {
          rowSpan: 2,
          imageType: 'logo',
          styles: { cellPadding: 0 }
        })
      ], 9.5),
      withRowHeight([
        borderedCell(formatContent.version, {
          colSpan: 3,
          styles: { fontSize: 5.3, fontStyle: 'bold' }
        }),
        borderedCell(
          formatContent.title,
          {
            colSpan: 24,
            styles: { fontSize: 7.5, fontStyle: 'bold' }
          }
        )
      ], 15),
      withRowHeight([
        borderedCell('', {
          colSpan: 8,
          borders: { bottom: 'medium' },
          styles: { cellPadding: 0 }
        }),
        borderedCell('', {
          colSpan: 20,
          borders: { bottom: 'medium' },
          styles: { cellPadding: 0 }
        })
      ], 7.5)
    ];

    doc.autoTable({
      startY: margin,
      body: brandRows,
      columnStyles,
      margin: pdfMargins,
      tableWidth,
      theme: 'plain',
      styles: {
        cellPadding: 1,
        font: 'helvetica',
        fontSize: 4.2,
        halign: 'center',
        lineColor: [0, 0, 0],
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        valign: 'middle'
      },
      didDrawCell: data => {
        if (data.cell.raw?.imageType === 'logo') {
          drawImageInCell(doc, logoDataUrl, data.cell, 38, 20);
        }
      }
    });

    const detailRows = [
      withRowHeight([
        blueBorderedCell('1. TEMA', {
          rowSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        borderedCell(getMeetingTopic(meeting), {
          rowSpan: 2
        }),
        blueBorderedCell('4. DATOS DE LA\nREUNIÓN', {
          rowSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('5. FECHA', {
          colSpan: 3,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('6. HORA INICIA', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('7. HORA\nTERMINA', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('8. LUGAR', {
          colSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('9. CIUDAD', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('10. COMUNA /\nCORREGIMIENTO', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('11. BARRIO / VEREDA /\nSECTOR', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('12. DIRECCIÓN', {
          colSpan: 2,
          styles: { fontStyle: 'bold' }
        })
      ], 14),
      withRowHeight([
        borderedCell('', {
          dateLabel: 'DÍA',
          dateValue: date[0] || ''
        }),
        borderedCell('', {
          dateLabel: 'MES',
          dateValue: date[1] || ''
        }),
        borderedCell('', {
          dateLabel: 'AÑO',
          dateValue: date[2] || ''
        }),
        borderedCell(
          formatTimeForReport(pickValue(meeting, 'startTime', 'start_time'))
        ),
        borderedCell(
          formatTimeForReport(pickValue(meeting, 'endTime', 'end_time'))
        ),
        borderedCell(getMeetingPlace(meeting, meetingGroup, meetingSchool), {
          colSpan: 2
        }),
        borderedCell('Medellín'),
        borderedCell(pickValue(meetingSchool?.municipality, 'name')),
        borderedCell(getMeetingNeighborhood(meeting, meetingSchool)),
        borderedCell(getMeetingAddress(meeting, meetingSchool), {
          colSpan: 2
        })
      ], 15),
      withRowHeight([
        blueBorderedCell('2. DEPENDENCIA /\nORGANIZADOR', {
          styles: { fontStyle: 'bold' }
        }),
        borderedCell(formatContent.organizer),
        blueBorderedCell('13. TIPO DE REUNIÓN\n(señale con X)', {
          rowSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Capacitación', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Entrenamiento', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Formación', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Informativa', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Seguimiento', {
          colSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Toma de decisiones', {
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('Otro tipo de reunión (¿cuál?)', {
          colSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('14. NOMBRE DE PROYECTO(S) DE INVERSIÓN', {
          colSpan: 2,
          styles: { fontStyle: 'bold' }
        }),
        blueBorderedCell('15. CÓDIGO(S) DE PROYECTO(S) DE INVERSIÓN', {
          styles: { fontStyle: 'bold' }
        })
      ], 24),
      withRowHeight([
        blueBorderedCell('3. RESPONSABLE', {
          styles: { fontStyle: 'bold' }
        }),
        borderedCell(
          getMeetingResponsible(meeting, meetingGroup, meetingSchool)
        ),
        borderedCell(''),
        borderedCell(''),
        borderedCell('X'),
        borderedCell(''),
        borderedCell('', { colSpan: 2 }),
        borderedCell(''),
        borderedCell('', { colSpan: 2 }),
        borderedCell(formatContent.projectName, { colSpan: 2 }),
        borderedCell(formatContent.projectCode)
      ], 18)
    ];

    doc.autoTable({
      startY: doc.lastAutoTable.finalY,
      body: detailRows,
      columnStyles: detailColumnStyles,
      margin: pdfMargins,
      tableWidth,
      theme: 'plain',
      styles: {
        cellPadding: 1,
        font: 'helvetica',
        fontSize: 4,
        halign: 'center',
        lineColor: [0, 0, 0],
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        valign: 'middle'
      },
      didDrawCell: data => {
        const raw = data.cell.raw;
        if (!raw?.dateLabel) return;

        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(3.6);
        doc.text(raw.dateLabel, data.cell.x + 1.2, data.cell.y + 4.2);

        if (raw.dateValue) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(4);
          doc.text(
            String(raw.dateValue),
            data.cell.x + data.cell.width / 2,
            data.cell.y + data.cell.height - 2.2,
            { align: 'center' }
          );
        }
      }
    });

    doc.autoTable({
      startY: doc.lastAutoTable.finalY,
      body: [
        withRowHeight([
          borderedCell('', {
            colSpan: 28,
            privacy: true,
            styles: { cellPadding: 0 }
          })
        ], 68)
      ],
      columnStyles,
      margin: pdfMargins,
      tableWidth,
      theme: 'plain',
      styles: {
        font: 'helvetica',
        lineColor: [0, 0, 0],
        overflow: 'hidden',
        textColor: [0, 0, 0]
      },
      didDrawCell: data => {
        if (!data.cell.raw?.privacy) return;
        const isFO093 = formatCode === 'FO-GINF-093';
        drawRichText(
          doc,
          formatContent.privacyParts,
          data.cell.x + 2,
          isFO093 ? data.cell.y : data.cell.y + 5,
          data.cell.width - 4,
          isFO093 ? 5 : 3.8,
          isFO093 ? 6.1 : 4.45,
          isFO093 ? data.cell.height : undefined
        );
      }
    });

    const attendanceCell = (
      content,
      {
        colSpan,
        fillColor = whiteFill,
        left = thinBorder,
        right = thinBorder,
        top = thinBorder,
        bottom = thinBorder,
        styles = {},
        ...metadata
      } = {}
    ) => ({
      content: content ?? '',
      ...metadata,
      ...(colSpan ? { colSpan } : {}),
      styles: {
        fillColor,
        lineColor: [0, 0, 0],
        lineWidth: { top, right, bottom, left },
        minCellHeight: 13.5,
        halign: 'center',
        valign: 'middle',
        ...styles
      }
    });

    const body = Array.from({ length: FO093_ROWS_PER_SHEET }, (_, index) => {
      const record = records[index];
      const beneficiary = record?.beneficiary || {};
      const populationMarks = record
        ? getFO093PopulationMarks(beneficiary).map(reportMark)
        : Array(15).fill('');

      return [
        attendanceCell(pageIndex * FO093_ROWS_PER_SHEET + index + 1, {
          left: mediumBorder
        }),
        attendanceCell(
          record ? getBeneficiaryDocumentType(beneficiary) : ''
        ),
        attendanceCell(record ? pickValue(beneficiary, 'document') : ''),
        attendanceCell(record ? getBeneficiaryName(beneficiary) : ''),
        attendanceCell(
          record
            ? getAttendanceCompany(beneficiary, formatCode, meeting)
            : ''
        ),
        attendanceCell(
          record
            ? getAttendanceRole(beneficiary, formatCode, meeting)
            : '',
          { right: mediumBorder }
        ),
        ...populationMarks.map((mark, populationIndex) =>
          attendanceCell(mark, {
            right: [1, 6, 13, 14].includes(populationIndex)
              ? mediumBorder
              : thinBorder
          })
        ),
        attendanceCell(
          record
            ? getBeneficiaryCommuneNumber(beneficiary)
            : '',
          { right: mediumBorder }
        ),
        attendanceCell(record ? getBeneficiaryEmail(beneficiary) : '', {
          colSpan: 3,
          right: mediumBorder,
          styles: { halign: 'center' }
        }),
        attendanceCell(
          record ? getAttendanceOrganization(beneficiary, meeting) : ''
        ),
        attendanceCell(record ? getBeneficiaryPhone(beneficiary) : ''),
        attendanceCell('', {
          right: mediumBorder,
          signatureDataUrl: record
            ? signatureDataUrls.get(getAttendanceSignatureId(record))
            : ''
        })
      ];
    });

    const populationTotals = showTotals
      ? records.reduce((totals, record) => {
          getFO093PopulationMarks(record?.beneficiary).forEach((marked, index) => {
            if (marked) totals[index] += 1;
          });
          return totals;
        }, Array(15).fill(0))
      : Array(15).fill('');
    body.push([
      attendanceCell('', {
        colSpan: 5,
        left: mediumBorder,
        top: mediumBorder,
        bottom: mediumBorder
      }),
      attendanceCell('32. TOTALES', {
        fillColor: grayFill,
        right: mediumBorder,
        top: mediumBorder,
        bottom: mediumBorder,
        styles: { fontStyle: 'bold', halign: 'left' }
      }),
      ...populationTotals.map((total, populationIndex) =>
        attendanceCell(total, {
          fillColor: grayFill,
          right: [1, 6, 13, 14].includes(populationIndex)
            ? mediumBorder
            : thinBorder,
          top: mediumBorder,
          bottom: mediumBorder,
          styles: { fontStyle: 'bold' }
        })
      ),
      attendanceCell('', {
        right: mediumBorder,
        top: mediumBorder,
        bottom: mediumBorder
      }),
      attendanceCell('', {
        colSpan: 6,
        right: mediumBorder,
        top: mediumBorder,
        bottom: mediumBorder
      })
    ]);
    body.push([
      attendanceCell(
        '* EN CASO DE QUE NO APLIQUE NINGUNA DENOMINACIÓN DE LOS ÍTEMS DE ENFOQUE DIFERENCIAL Y DERECHOS, DEJAR EN BLANCO.',
        {
          colSpan: 28,
          left: mediumBorder,
          right: mediumBorder,
          bottom: mediumBorder,
          styles: {
            fontSize: 4.2,
            fontStyle: 'bold',
            halign: 'left',
            minCellHeight: 18,
            textColor: [0, 32, 96]
          }
        }
      )
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY,
      head: headerRows,
      body,
      theme: 'plain',
      margin: pdfMargins,
      tableWidth,
      columnStyles,
      styles: {
        cellPadding: 0.8,
        font: 'helvetica',
        fontSize: 4,
        lineColor: [0, 0, 0],
        lineWidth: thinBorder,
        minCellHeight: 0,
        overflow: 'ellipsize',
        textColor: [0, 0, 0],
        valign: 'middle'
      },
      headStyles: {
        cellPadding: 0.7,
        fontSize: 4,
        fontStyle: 'bold',
        halign: 'center',
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        valign: 'middle'
      },
      didDrawCell: data => {
        const raw = data.cell.raw;
        if (raw?.verticalLabel) {
          doc.setFont('helvetica', 'bold');
          const requestedFontSize = raw.verticalFontSize || 3.5;
          const availableHeight = Math.max(data.cell.height - 4, 1);
          doc.setFontSize(requestedFontSize);
          const requestedTextWidth = doc.getTextWidth(raw.verticalLabel);
          const fittedFontSize =
            requestedTextWidth > availableHeight
              ? requestedFontSize * (availableHeight / requestedTextWidth)
              : requestedFontSize;
          doc.setFontSize(fittedFontSize);
          const fittedTextWidth = doc.getTextWidth(raw.verticalLabel);
          const centerX = data.cell.x + data.cell.width / 2;
          const centeredBaselineY =
            data.cell.y + (data.cell.height + fittedTextWidth) / 2;
          doc.text(
            raw.verticalLabel,
            centerX + fittedFontSize * 0.28,
            centeredBaselineY,
            { angle: 90 }
          );
        }
        if (raw?.arrow) {
          const centerX = data.cell.x + data.cell.width / 2;
          const arrowTop = data.cell.y + data.cell.height * 0.25;
          const arrowBottom = data.cell.y + data.cell.height * 0.72;
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.55);
          doc.line(centerX, arrowTop, centerX, arrowBottom);
          doc.line(centerX, arrowBottom, centerX - 2, arrowBottom - 2.5);
          doc.line(centerX, arrowBottom, centerX + 2, arrowBottom - 2.5);
        }
        if (raw?.signatureDataUrl) {
          drawImageInCell(
            doc,
            raw.signatureDataUrl,
            data.cell,
            48,
            11
          );
        }
      }
    });
  });

  if (download) {
    doc.save(buildReportFileName(meeting));
    return null;
  }

  return doc.output('blob');
}

async function generateFO086Pdf({
  meeting,
  meetingGroup,
  meetingSchool,
  sheets,
  attendedRecords,
  download = true
}) {
  if (Array.isArray(sheets)) {
    return generateFO093Pdf({
      meeting,
      meetingGroup,
      meetingSchool,
      sheets,
      attendedRecords,
      formatCode: 'FO-GINF-086',
      download
    });
  }

  const doc = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter',
    compress: true
  });
  const signatureDataUrls = await loadSignatureDataUrls(attendedRecords);
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Cód. FO-GINF-086', pageWidth / 2, 22, { align: 'center' });
  doc.setFontSize(11);
  doc.text('LISTADO DE ASISTENCIA CON CARACTERIZACIÓN', pageWidth / 2, 36, { align: 'center' });
  doc.setFontSize(6.5);
  const topicLines = doc.splitTextToSize(`Tema: ${getMeetingTopic(meeting)}`, 325);
  const topicExtraHeight = (topicLines.length - 1) * 8;
  doc.text(topicLines, 24, 53);
  doc.text(`Responsable: ${getMeetingResponsible(meeting, meetingGroup, meetingSchool)}`, 24, 64 + topicExtraHeight);
  doc.text(`Fecha: ${formatDateForReport(meeting.meeting_date || meeting.meetingDate)}`, 360, 53);
  doc.text(`Lugar: ${getMeetingPlace(meeting, meetingGroup, meetingSchool)}`, 360, 64 + topicExtraHeight);

  const headers = ['#','TD','Documento','Nombres','Entidad','Cargo','Sexo','0-5','6-17','18-28','29-54','55+','Afro','Indíg.','ROM','LGTBI','Calle','Campes.','Discap.','Víct.','Comuna','Correo','Teléfono','Firma'];
  const body = attendedRecords.map((record, rowIndex) => {
    const beneficiary = record?.beneficiary || {};
    const gender = normalizeReportValue(pickValue(beneficiary, 'gender'));
    return [
      rowIndex + 1, getBeneficiaryDocumentType(beneficiary), pickValue(beneficiary, 'document'),
      getBeneficiaryName(beneficiary), pickValue(beneficiary, 'observations') || 'Particular',
      pickValue(beneficiary, 'occupationValue', 'occupation_value') || 'Participante',
      gender.includes('mujer') || gender.includes('femenino') ? 'F' : 'M',
      ...getFO093PopulationMarks(beneficiary).slice(2).map(reportMark),
      beneficiary?.municipality?.name || pickValue(beneficiary, 'municipalityId', 'municipality_id'),
      getBeneficiaryEmail(beneficiary), getBeneficiaryPhone(beneficiary), ''
    ];
  });
  doc.autoTable({
    startY: 76 + topicExtraHeight,
    head: [headers],
    body,
    theme: 'grid',
    margin: 20,
    styles: { font: 'helvetica', fontSize: 4.8, cellPadding: 1.5, lineColor: 0, lineWidth: .35, minCellHeight: 22 },
    headStyles: { fillColor: [237, 242, 247], textColor: 0, fontStyle: 'bold', halign: 'center' },
    didDrawCell: data => {
      const record = attendedRecords[data.row.index];
      const imageId = record ? getAttendanceSignatureId(record) : null;
      if (data.section === 'body' && data.column.index === 23 && imageId) {
        drawImageInCell(doc, signatureDataUrls.get(imageId), data.cell, 45, 17);
      }
    }
  });
  doc.save(buildReportFileName(meeting));
}

export async function buildOfficialAttendancePdfBlob(
  meeting,
  attendances = [],
  participants = attendances
) {
  const attendedRecords = attendances.filter(isSignedAttendance);
  return buildAttendancePdfForRecords(meeting, attendedRecords, { participants });
}

export async function buildFO093AttendancePdfBlob(meeting, attendances = []) {
  if (!meeting || attendances.length === 0) return null;
  const meetingGroup = meeting.group || {};
  const meetingSchool = meeting.school || meetingGroup.school || {};
  return generateFO093Pdf({
    meeting,
    meetingGroup,
    meetingSchool,
    sheets: chunkRecords(attendances, FO093_ROWS_PER_SHEET),
    attendedRecords: attendances,
    formatCode: 'FO-GINF-093',
    download: false
  });
}

export async function buildPrefilledAttendancePdfBlob(meeting, beneficiaries = []) {
  // Only beneficiary details are passed: saved signatures are never included.
  const records = beneficiaries.map(beneficiary => ({ beneficiary }));
  return buildAttendancePdfForRecords(meeting, records, {
    showFO093Totals: false,
    participants: beneficiaries
  });
}

async function buildAttendancePdfForRecords(
  meeting,
  attendedRecords,
  { showFO093Totals = true, participants = attendedRecords } = {}
) {
  if (!meeting || attendedRecords.length === 0) return null;

  const meetingGroup =
    meeting.group ||
    meeting.activityGroup ||
    meeting.activity_group ||
    {};
  const meetingSchool =
    meeting.school ||
    meetingGroup.school ||
    (
      meeting.activityGroup || meeting.activity_group
        ? {
            name:
              meetingGroup.place ||
              meetingGroup.activity?.name ||
              meetingGroup.code,
            address: meetingGroup.address || '',
            municipality: { name: meetingGroup.comuna },
            neighborhood: { name: meetingGroup.barrio },
            organization: meetingGroup.activity?.line,
            user: meetingGroup.user
          }
        : {}
    );
  const [formatCode] = getAllowedAttendanceFormatCodes(meeting, participants);
  if (!formatCode) return null;

  if (formatCode === 'FO-GINF-093') {
    return generateFO093Pdf({
      meeting,
      meetingGroup,
      meetingSchool,
      sheets: chunkRecords(attendedRecords, FO093_ROWS_PER_SHEET),
      attendedRecords,
      formatCode,
      download: false,
      showTotals: showFO093Totals
    });
  }

  if (formatCode === 'FO-GINF-086') {
    return generateFO086Pdf({
      meeting,
      meetingGroup,
      meetingSchool,
      sheets: chunkRecords(attendedRecords, FO093_ROWS_PER_SHEET),
      attendedRecords,
      download: false
    });
  }

  return generateAttendancePdf({
    meeting,
    meetingGroup,
    meetingSchool,
    sheets: chunkRecords(attendedRecords, ROWS_PER_SHEET),
    attendedRecords,
    formatCode,
    download: false
  });
}

function ColGroup() {
  const totalWidth = FO083_COLUMN_WIDTHS.reduce((sum, width) => sum + width, 0);

  return (
    <colgroup>
      {FO083_COLUMN_WIDTHS.map((width, index) => (
        <col
          key={index}
          style={{ width: `${(width / totalWidth) * 100}%` }}
        />
      ))}
    </colgroup>
  );
}

function CheckCell({ checked }) {
  return (
    <td colSpan="2" className="fo083-check-cell">
      {checked ? 'X' : ''}
    </td>
  );
}

function PrivacyNotice() {
  return (
    <>
      {NNA_PRIVACY_NOTICE_PARTS.map((part, index) => {
        return (
          <span
            key={index}
            className={part.bold ? 'fo083-privacy-text fo083-privacy-strong' : 'fo083-privacy-text'}
          >
            {part.text}
          </span>
        );
      })}
    </>
  );
}

function AttendanceRow({ attendance, rowNumber }) {
  const beneficiary = attendance?.beneficiary || {};
  const signatureImageId = attendance?.image?.id;

  return (
    <tr className="fo083-attendance-row">
      <td>{rowNumber}</td>
      <td>{attendance ? getBeneficiaryDocumentType(beneficiary) : ''}</td>
      <td colSpan="3">{attendance ? pickValue(beneficiary, 'document') : ''}</td>
      <td colSpan="10" className="fo083-bold">
        {attendance ? getBeneficiaryName(beneficiary) : ''}
      </td>
      <td colSpan="4">{attendance ? getBeneficiaryBirthDate(beneficiary) : ''}</td>
      <td colSpan="3">{attendance ? getBeneficiaryPhone(beneficiary) : ''}</td>
      <td colSpan="10">
        {attendance ? getBeneficiaryEmail(beneficiary) : ''}
      </td>
      <td colSpan="6" className="fo083-signature-cell">
        {signatureImageId ? (
          <img
            src={buildProtectedImageUrl(signatureImageId, false)}
            alt="Firma"
            className="fo083-signature"
          />
        ) : null}
      </td>
    </tr>
  );
}

function AttendanceSheet({
  pageIndex,
  totalPages,
  records,
  meeting,
  meetingGroup,
  meetingSchool,
  formatCode
}) {
  const rowSlots = Array.from({ length: ROWS_PER_SHEET }, (_, index) => ({
    record: records[index] || null,
    rowNumber: pageIndex * ROWS_PER_SHEET + index + 1
  }));
  const meetingDate = formatDateForReport(meeting.meeting_date || meeting.meetingDate);
  const meetingStartTime =
    formatTimeForReport(pickValue(meeting, 'startTime', 'start_time')) || '';
  const meetingEndTime =
    formatTimeForReport(pickValue(meeting, 'endTime', 'end_time')) || '';

  return (
    <section className="fo083-sheet">
      <table className="fo083-table">
        <ColGroup />
        <tbody>
          <tr className="fo083-top-row">
            <td colSpan="8" className="fo083-code-cell">
              Cód. {formatCode}
            </td>
            <td colSpan="24" rowSpan="2" className="fo083-title-cell">
              Formato
              <br />
              FO-GINF Asistencia a reuniones, eventos y capacitaciones de niñas, niños y adolescentes con autorización previa del tratamiento por parte de sus Representantes Legales
            </td>
            <td colSpan="6" rowSpan="2" className="fo083-logo-cell">
              <img src={medellinLogo} alt="Alcaldía de Medellín" />
            </td>
          </tr>
          <tr className="fo083-version-row">
            <td colSpan="8" className="fo083-code-cell">
              Versión 2
            </td>
          </tr>
          <tr>
            <td colSpan="4" className="fo083-label-cell">Tema</td>
            <td colSpan="24" className="fo083-value-cell">
              {getMeetingTopic(meeting)}
            </td>
            <td colSpan="3" rowSpan="3" className="fo083-label-cell fo083-center">
              Tipo de Reunión
            </td>
            <td colSpan="5" className="fo083-option-cell">Capacitación</td>
            <CheckCell checked />
          </tr>
          <tr>
            <td colSpan="4" className="fo083-label-cell">Dependencia</td>
            <td colSpan="24" className="fo083-value-cell">
              Secretaría de Participación Ciudadana/ Subsecretaría de Formación y Participación Ciudadana/ Unidad de Gestión Formativa
            </td>
            <td colSpan="5" className="fo083-option-cell">Reunion</td>
            <CheckCell checked={false} />
          </tr>
          <tr>
            <td colSpan="4" className="fo083-label-cell">Responsable</td>
            <td colSpan="24" className="fo083-value-cell">
              {getMeetingResponsible(meeting, meetingGroup, meetingSchool)}
            </td>
            <td colSpan="5" className="fo083-option-cell">Evento</td>
            <CheckCell checked={false} />
          </tr>
          <tr>
            <td colSpan="4" className="fo083-label-cell">Fecha</td>
            <td colSpan="4" className="fo083-value-cell">{meetingDate}</td>
            <td colSpan="3" className="fo083-label-cell">Lugar</td>
            <td colSpan="10" className="fo083-value-cell">
              {getMeetingPlace(meeting, meetingGroup, meetingSchool)}
            </td>
            <td colSpan="3" className="fo083-label-cell">Hora inicia</td>
            <td colSpan="4" className="fo083-value-cell">{meetingStartTime}</td>
            <td colSpan="3" className="fo083-label-cell">Hora termina</td>
            <td colSpan="7" className="fo083-value-cell">{meetingEndTime}</td>
          </tr>
          <tr>
            <td colSpan="38" className="fo083-privacy-cell">
              <PrivacyNotice />
            </td>
          </tr>
          <tr className="fo083-attendance-header">
            <td>N°</td>
            <td className="fo083-doc-type-header">
              Tipo
              <br />
              Dcto
            </td>
            <td colSpan="3">Número de Documento</td>
            <td colSpan="10">Nombres y Apellidos</td>
            <td colSpan="4">Fecha Nacimiento<br />(dd/mm/aaaa)</td>
            <td colSpan="3">Teléfono Fijo / Número Celular</td>
            <td colSpan="10">Correo Electrónico</td>
            <td colSpan="6">Firma</td>
          </tr>
          {rowSlots.map(({ record, rowNumber }) => (
            <AttendanceRow
              key={rowNumber}
              attendance={record}
              rowNumber={rowNumber}
            />
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="fo083-page-number">
          Página {pageIndex + 1} de {totalPages}
        </div>
      )}
    </section>
  );
}

function FO093AttendanceRow({ attendance, rowNumber, formatCode, meeting }) {
  const beneficiary = attendance?.beneficiary || {};
  const populationMarks = attendance
    ? getFO093PopulationMarks(beneficiary)
    : Array(15).fill(false);
  const signatureImageId = attendance?.image?.id;

  return (
    <tr className="fo093-person-row">
      <td>{rowNumber}</td>
      <td>{attendance ? getBeneficiaryDocumentType(beneficiary) : ''}</td>
      <td>{attendance ? pickValue(beneficiary, 'document') : ''}</td>
      <td>{attendance ? getBeneficiaryName(beneficiary) : ''}</td>
      <td>{attendance ? getAttendanceCompany(beneficiary, formatCode, meeting) : ''}</td>
      <td className="medium-right">{attendance ? getAttendanceRole(beneficiary, formatCode, meeting) : ''}</td>
      {populationMarks.map((marked, index) => (
        <td
          key={index}
          className={[1, 6, 13, 14].includes(index) ? 'medium-right' : ''}
        >
          {reportMark(marked)}
        </td>
      ))}
      <td className="medium-right">{attendance ? getBeneficiaryCommuneNumber(beneficiary) : ''}</td>
      <td colSpan="3" className="medium-right">{attendance ? getBeneficiaryEmail(beneficiary) : ''}</td>
      <td>{attendance ? getAttendanceOrganization(beneficiary, meeting) : ''}</td>
      <td>{attendance ? getBeneficiaryPhone(beneficiary) : ''}</td>
      <td className="fo093-signature-cell medium-right">
        {signatureImageId ? <img src={buildProtectedImageUrl(signatureImageId, false)} alt="Firma" /> : null}
      </td>
    </tr>
  );
}

function FO093Sheet({
  pageIndex,
  records,
  meeting,
  meetingGroup,
  meetingSchool,
  formatCode = 'FO-GINF-093'
}) {
  const formatContent = getCharacterizationFormatContent(formatCode);
  const indicator = getAttendanceIndicator(formatCode);
  const slots = Array.from({ length: FO093_ROWS_PER_SHEET }, (_, index) => records[index] || null);
  const populationTotals = records.reduce((totals, record) => {
    getFO093PopulationMarks(record?.beneficiary).forEach((marked, index) => {
      if (marked) totals[index] += 1;
    });
    return totals;
  }, Array(15).fill(0));
  const date = formatDateForReport(meeting.meeting_date || meeting.meetingDate).split('/');
  return (
    <section className="fo093-sheet">
      <table className="fo093-table">
        <colgroup>
          {FO093_HEADER_COLUMN_WIDTHS.map((width, index) => (
            <col
              key={index}
              style={{ width: `${width}%` }}
            />
          ))}
        </colgroup>
        <tbody>
          <tr className="fo093-brand-row">
            <td colSpan="3" className="fo093-header-code"><strong>Cód. {formatCode}</strong></td>
            <td colSpan="24" className="fo093-header-format">Formato</td>
            <td rowSpan="2"><img src={medellinLogo} alt="Alcaldía de Medellín" /></td>
          </tr>
          <tr className="fo093-brand-title-row">
            <td colSpan="3" className="fo093-header-version"><strong>{formatContent.version}</strong></td>
            <td colSpan="24" className="fo093-header-title"><strong>{formatContent.title}</strong></td>
          </tr>
          <tr className="fo093-brand-spacer-row">
            <td colSpan="8"></td>
            <td colSpan="20"></td>
          </tr>
          <tr>
            <td colSpan="28" className="fo093-details-cell">
              <div className="fo093-details-grid">
                <div className="label comment topic">1. TEMA</div>
                <div className="value topic-value">{getMeetingTopic(meeting)}</div>
                <div className="label meeting-data">4. DATOS DE LA<br />REUNIÓN</div>
                <div className="label date-title comment">5. FECHA</div>
                <div className="label start-title">6. HORA INICIA</div>
                <div className="label end-title">7. HORA<br />TERMINA</div>
                <div className="label place-title comment">8. LUGAR</div>
                <div className="label city-title">9. CIUDAD</div>
                <div className="label commune-title comment">10. COMUNA /<br />CORREGIMIENTO</div>
                <div className="label neighborhood-title comment">11. BARRIO / VEREDA /<br />SECTOR</div>
                <div className="label address-title">12. DIRECCIÓN</div>

                <div className="value date-day"><b>DÍA</b><span>{date[0] || ''}</span></div>
                <div className="value date-month"><b>MES</b><span>{date[1] || ''}</span></div>
                <div className="value date-year"><b>AÑO</b><span>{date[2] || ''}</span></div>
                <div className="value start-value">{formatTimeForReport(pickValue(meeting, 'startTime', 'start_time'))}</div>
                <div className="value end-value">{formatTimeForReport(pickValue(meeting, 'endTime', 'end_time'))}</div>
                <div className="value place-value">{getMeetingPlace(meeting, meetingGroup, meetingSchool)}</div>
                <div className="value city-value">Medellín</div>
                <div className="value commune-value">{pickValue(meetingSchool?.municipality, 'name')}</div>
                <div className="value neighborhood-value">{getMeetingNeighborhood(meeting, meetingSchool)}</div>
                <div className="value address-value">{getMeetingAddress(meeting, meetingSchool)}</div>

                <div className="label comment dependency">2. DEPENDENCIA /<br />ORGANIZADOR</div>
                <div className="value dependency-value">{formatContent.organizer}</div>
                <div className="label meeting-type">13. TIPO DE REUNIÓN<br />(señale con X)</div>
                {['Capacitación','Entrenamiento','Formación','Informativa','Seguimiento','Toma de decisiones','Otro tipo de reunión (¿cuál?)'].map((text, index) => <div key={text} className={`label type-${index + 1}`}>{text}</div>)}
                <div className="label project-title">14. NOMBRE DE PROYECTO(S) DE INVERSIÓN</div>
                <div className="label project-code-title">15. CÓDIGO(S) DE PROYECTO(S) DE INVERSIÓN</div>

                <div className="label comment responsible">3. RESPONSABLE</div>
                <div className="value responsible-value">{getMeetingResponsible(meeting, meetingGroup, meetingSchool)}</div>
                <div className="value mark-1"></div><div className="value mark-2"></div><div className="value mark-3">X</div>
                <div className="value mark-4"></div><div className="value mark-5"></div><div className="value mark-6"></div><div className="value mark-7"></div>
                <div className="value project-value">{formatContent.projectName}</div>
                <div className="value project-code-value">{formatContent.projectCode}</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <table className="fo093-table fo093-attendance-table">
        <colgroup>
          {FO093_HEADER_COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <tbody>
          <tr>
            <td colSpan="28" className="fo093-privacy">
              {formatCode === 'FO-GINF-086' ? (
                <FO086PrivacyNotice />
              ) : (
                <FO093PrivacyNotice />
              )}
            </td>
          </tr>
          <tr className="fo093-roster-groups">
            <ExcelBorderCell className="fo093-blue-cell" borders={{ top: 'medium', left: 'medium', bottom: 'medium' }} />
            <ExcelBorderCell className="fo093-blue-cell" borders={{ top: 'medium', bottom: 'medium' }} />
            <ExcelBorderCell as="th" colSpan="4" className="fo093-blue-cell" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>IDENTIFICACIÓN</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="15" className="fo093-gray-cell" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>DESAGREGACIÓN POBLACIONAL</ExcelBorderCell>
            <ExcelBorderCell className="fo093-gray-cell" borders={{ top: 'medium', left: 'medium', right: 'medium' }} />
            <ExcelBorderCell as="th" colSpan="3" className="fo093-gray-cell" borders={{ top: 'medium', right: 'medium', bottom: 'medium' }}>DESAGREGACIÓN TERRITORIAL</ExcelBorderCell>
            <ExcelBorderCell borders={{ top: 'medium', left: 'medium', bottom: 'medium' }} />
            <ExcelBorderCell borders={{ top: 'medium', right: 'thin', bottom: 'medium' }} />
            <ExcelBorderCell borders={{ top: 'medium', left: 'thin', right: 'medium', bottom: 'medium' }} />
          </tr>
          <tr className="fo093-roster-help">
            <ExcelBorderCell className="fo093-blue-cell" borders={{ top: 'medium', left: 'medium', right: 'medium' }} />
            <ExcelBorderCell as="th" className="fo093-blue-cell fo093-arrow" borders={{ top: 'medium', left: 'medium' }}>↓</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="4" className="fo093-blue-cell" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>19. TIPO DE DOCUMENTO (TD) (detalle)</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="2" className="fo093-gray-cell fo093-left-label" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>24. SEXO</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="5" className="fo093-gray-cell fo093-left-label" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>25. RANGO EDAD</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="7" className="fo093-gray-cell fo093-left-label" borders={{ top: 'medium', bottom: 'medium' }}>26. ENFOQUE DIFERENCIAL*</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-gray-cell fo093-left-label" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>DERE<br />CHOS*</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-gray-cell fo093-arrow" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>↓</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="3" className="fo093-gray-cell fo093-left-label" borders={{ top: 'medium', right: 'medium', bottom: 'medium' }}>27. COMUNAS Y CORREGIMIENTOS (DETALLE)</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="2" className="fo093-gray-cell" borders={{ right: 'medium', bottom: 'medium' }}>16. NOMBRE(S) DE INDICADOR(ES) DE PLAN DE DLLO (PDM) / PLAN INDICATIVO (PI) - (Diligencia el organizador)</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-gray-cell" borders={{ left: 'medium', right: 'medium', bottom: 'medium' }}>17. CÓDIGO(S) DE INDICADOR(ES) DE PDM Y PI</ExcelBorderCell>
          </tr>
          <tr className="fo093-roster-detail">
            <ExcelBorderCell as="th" className="fo093-blue-cell fo093-vertical-text" borders={{ left: 'medium', right: 'medium' }}>
              <span className="fo093-vertical-label">CONSECUTIVO</span>
            </ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell fo093-vertical-text" borders={{ left: 'medium', right: 'medium' }}>
              <span className="fo093-vertical-label">Tipo de Documento</span>
            </ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="2" className="fo093-blue-cell fo093-document-help" borders={{ top: 'medium', left: 'medium', bottom: 'medium' }}>{'\nCC    = Cédula de ciudadanía\nTI      =  Tarjeta de identidad \nCE    = Cédula de extranjería'}</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="2" className="fo093-blue-cell fo093-document-help" borders={{ top: 'medium', right: 'medium', bottom: 'medium' }}>{'RC    = Registro civil\nNUIP = Número Único de Identificación Personal\nNIP   = Número de Identificación Personal'}</ExcelBorderCell>
            {[
              ['Hombre', { top: 'medium', left: 'medium', right: 'thin', bottom: 'medium' }],
              ['Mujer', { top: 'medium', left: 'thin', right: 'medium', bottom: 'thin' }],
              ['Primera infancia 0-5', { top: 'medium', left: 'medium', right: 'thin', bottom: 'thin' }],
              ['Niñez-Adolescencia 6-17', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Juventud 18-28', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Adulto/a 29-54', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Adulto Mayor 55 o más', { top: 'medium', left: 'thin', right: 'medium', bottom: 'thin' }],
              ['Afrodescendiente', { top: 'medium', right: 'thin', bottom: 'thin' }],
              ['Indígena', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['ROM', { top: 'medium', left: 'thin', right: 'thin', bottom: 'medium' }],
              ['LGTBI', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Situación de calle', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Campesino', { top: 'medium', left: 'thin', right: 'thin', bottom: 'thin' }],
              ['Discapacidad', { top: 'medium', left: 'thin', bottom: 'thin' }],
              ['Población víctima', { top: 'medium', left: 'medium', right: 'medium', bottom: 'thin' }]
            ].map(([text, borders]) => (
              <ExcelBorderCell
                key={text}
                as="th"
                rowSpan="2"
                className="fo093-blue-cell fo093-vertical-text"
                borders={{ ...borders, bottom: 'medium' }}
              >
                <span className={`fo093-vertical-label${text.length > 20 ? ' fo093-vertical-label-compact' : ''}`}>
                  {text}
                </span>
              </ExcelBorderCell>
            ))}
            <ExcelBorderCell as="th" rowSpan="2" className="fo093-blue-cell fo093-vertical-text" borders={{ left: 'medium', right: 'medium', bottom: 'medium' }}>
              <span className="fo093-vertical-label fo093-vertical-label-compact">27. # COMUNA DONDE RESIDE</span>
            </ExcelBorderCell>
            {[
              '1. Popular\n2. Santa Cruz\n3. Manrique\n4. Aranjuez\n5. Castilla\n6.12Octubre\n7.Robledo\n8. Villahermosa',
              '9. Buenos Aires\n10.La Candelaria\n11. Laureles Estadio\n12. La América\n13. San Javier\n14. El Poblado\n15. Guayabal\n16. Belén',
              '50.San Sebastián de Palmitas\n60. San Cristóbal\n70. AltaVista\n80. San Ant. de Prado\n90. Santa Elena.'
            ].map((text, index) => (
              <ExcelBorderCell
                key={index}
                className={`fo093-gray-cell fo093-commune-column${index === 2 ? ' fo093-commune-column-center' : ''}`}
                borders={index === 2 ? { right: 'medium' } : {}}
              >
                {text}
              </ExcelBorderCell>
            ))}
            <ExcelBorderCell colSpan="2" className="fo093-indicator-value" borders={{ top: 'thin', left: 'medium', right: 'thin', bottom: 'medium' }}>{indicator.name}</ExcelBorderCell>
            <ExcelBorderCell className="fo093-indicator-value" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>{indicator.code}</ExcelBorderCell>
          </tr>
          <tr className="fo093-roster-fields">
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ top: 'medium', left: 'medium', right: 'medium', bottom: 'medium' }}>18. No.</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ top: 'medium', left: 'thin', right: 'medium', bottom: 'medium' }}>19. TD</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ right: 'thin', bottom: 'medium' }}>20. NÚMERO DE DOCUMENTO</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ left: 'thin', right: 'medium', bottom: 'medium' }}>21. NOMBRES Y APELLIDOS</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ left: 'thin', right: 'medium', bottom: 'medium' }}>22. EMPRESA / ENTIDAD / DEPENDENCIA</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ left: 'thin', right: 'medium', bottom: 'medium' }}>23. CARGO / EMPLEO / CALIDAD EN LA QUE ACTÚA</ExcelBorderCell>
            <ExcelBorderCell as="th" colSpan="3" className="fo093-blue-cell" borders={{ top: 'medium', right: 'medium', bottom: 'medium' }}>28. CORREO ELECTRÓNICO</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ right: 'thin', bottom: 'medium' }}>{getAttendanceOrganizationHeader(formatCode)}</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ right: 'medium', bottom: 'medium' }}>30. TELÉFONO FIJO / NÚMERO DE CELULAR</ExcelBorderCell>
            <ExcelBorderCell as="th" className="fo093-blue-cell" borders={{ left: 'medium', right: 'medium', bottom: 'medium' }}>31. FIRMA</ExcelBorderCell>
          </tr>
          {slots.map((record, index) => (
            <FO093AttendanceRow
              key={index}
              attendance={record}
              rowNumber={pageIndex * FO093_ROWS_PER_SHEET + index + 1}
              formatCode={formatCode}
              meeting={meeting}
            />
          ))}
          <tr className="fo093-totals-row medium-bottom">
            <td colSpan="5"></td>
            <th className="medium-right">32. TOTALES</th>
            {populationTotals.map((total, index) => (
              <td key={index} className={[1, 6, 13, 14].includes(index) ? 'medium-right' : ''}>{total}</td>
            ))}
            <td className="medium-right"></td>
            <td colSpan="6" className="medium-right"></td>
          </tr>
          <tr className="fo093-note"><td colSpan="28">* EN CASO DE QUE NO APLIQUE NINGUNA DENOMINACIÓN DE LOS ÍTEMS DE ENFOQUE DIFERENCIAL Y DERECHOS, DEJAR EN BLANCO.</td></tr>
        </tbody>
      </table>
    </section>
  );
}

function FO086Sheet({
  pageIndex,
  records,
  meeting,
  meetingGroup,
  meetingSchool,
  useLegacyLayout = false
}) {
  if (!useLegacyLayout) {
    return (
      <FO093Sheet
        pageIndex={pageIndex}
        records={records}
        meeting={meeting}
        meetingGroup={meetingGroup}
        meetingSchool={meetingSchool}
        formatCode="FO-GINF-086"
      />
    );
  }

  const date = formatDateForReport(meeting.meeting_date || meeting.meetingDate);
  const marks = records.map(record => getFO093PopulationMarks(record?.beneficiary));
  const totals = Array.from({ length: 15 }, (_, index) =>
    marks.reduce((sum, row) => sum + (row[index] ? 1 : 0), 0)
  );

  return (
    <section className="fo086-sheet">
      <header className="fo086-header">
        <div>Cód. FO-GINF-086</div>
        <strong>LISTADO DE ASISTENCIA CON CARACTERIZACIÓN</strong>
      </header>
      <table className="fo086-info">
        <tbody>
          <tr>
            <td colSpan="3"><b>1. TEMA</b><span>{getMeetingTopic(meeting)}</span></td>
            <td><b>4. DATOS DE LA REUNIÓN</b><span>{date}</span></td>
            <td><b>5. HORA INICIA</b><span>{formatTimeForReport(pickValue(meeting, 'startTime', 'start_time'))}</span></td>
            <td><b>6. HORA TERMINA</b><span>{formatTimeForReport(pickValue(meeting, 'endTime', 'end_time'))}</span></td>
            <td><b>7. LUGAR</b><span>{getMeetingPlace(meeting, meetingGroup, meetingSchool)}</span></td>
          </tr>
          <tr>
            <td colSpan="2"><b>2. DEPENDENCIA / ORGANIZADOR</b><span>Secretaría de Participación Ciudadana</span></td>
            <td><b>3. RESPONSABLE</b><span>{getMeetingResponsible(meeting, meetingGroup, meetingSchool)}</span></td>
            <td><b>8. CIUDAD</b><span>Medellín</span></td>
            <td><b>9. COMUNA / CORREGIMIENTO</b><span>{meetingSchool?.municipality?.name || ''}</span></td>
            <td><b>10. BARRIO / VEREDA / SECTOR</b><span>{meetingSchool?.neighborhood?.name || ''}</span></td>
            <td><b>11. DIRECCIÓN</b><span>{getMeetingAddress(meeting, meetingSchool)}</span></td>
          </tr>
          <tr>
            <td colSpan="3"><b>13. TIPO DE REUNIÓN</b><span>[ ] Capacitación &nbsp; [X] Formación &nbsp; [ ] Informativa &nbsp; [ ] Seguimiento</span></td>
            <td colSpan="2"><b>14. PROYECTO DE INVERSIÓN</b><span>Formación Ciudadana</span></td>
            <td colSpan="2"><b>15. CÓDIGO DEL PROYECTO</b><span>240187</span></td>
          </tr>
        </tbody>
      </table>
      <div className="fo086-privacy">
        <b>AVISO DE PRIVACIDAD Y AUTORIZACIÓN:</b> En cumplimiento de la Ley 1581 de 2012 y sus normas reglamentarias y complementarias autorizo al Distrito Especial de Ciencia, Tecnología e Innovación de Medellín como responsable para tratar mis datos personales, con las finalidades de registrar la asistencia a eventos, reuniones o actividades institucionales y realizar comunicaciones relacionadas. La entrega de datos sensibles es facultativa. MI AUTORIZACIÓN SE PERFECCIONA al diligenciar y/o firmar este documento.
      </div>
      <table className="fo086-attendance">
        <thead>
          <tr>
            <th rowSpan="2">#</th><th rowSpan="2">TD</th><th rowSpan="2">Documento</th>
            <th rowSpan="2">Nombres y apellidos</th><th rowSpan="2">Entidad / Organización</th>
            <th rowSpan="2">Cargo / Calidad</th><th rowSpan="2">Sexo</th>
            <th colSpan="5">Rango de edad</th><th colSpan="8">Enfoques diferenciales</th>
            <th rowSpan="2">Comuna</th><th rowSpan="2">Correo electrónico</th>
            <th rowSpan="2">Teléfono</th><th rowSpan="2">Firma digital</th>
          </tr>
          <tr>
            {['0-5','6-17','18-28','29-54','55+','Afro','Indíg.','ROM','LGTBI','Calle','Campes.','Discap.','Víct.'].map(label => <th key={label}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => {
            const beneficiary = record?.beneficiary || {};
            const gender = normalizeReportValue(pickValue(beneficiary, 'gender'));
            return (
              <tr key={record?.id || index}>
                <td>{index + 1}</td><td>{getBeneficiaryDocumentType(beneficiary)}</td>
                <td>{pickValue(beneficiary, 'document')}</td><td>{getBeneficiaryName(beneficiary)}</td>
                <td>{pickValue(beneficiary, 'observations') || 'Particular'}</td>
                <td>{pickValue(beneficiary, 'occupationValue', 'occupation_value') || 'Participante'}</td>
                <td>{gender.includes('mujer') || gender.includes('femenino') ? 'F' : 'M'}</td>
                {marks[index].slice(2).map((marked, markIndex) => <td key={markIndex}>{reportMark(marked)}</td>)}
                <td>{beneficiary?.municipality?.name || pickValue(beneficiary, 'municipalityId', 'municipality_id')}</td>
                <td>{getBeneficiaryEmail(beneficiary)}</td><td>{getBeneficiaryPhone(beneficiary)}</td>
                <td>{record?.image?.id ? <img src={buildProtectedImageUrl(record.image.id, false)} alt="Firma" /> : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table className="fo086-statistics">
        <tbody>
          <tr><th colSpan="2">TOTAL GENERAL</th><td>{records.length}</td><th>Hombres</th><td>{totals[0]}</td><th>Mujeres</th><td>{totals[1]}</td><th>0-5</th><td>{totals[2]}</td><th>6-17</th><td>{totals[3]}</td><th>18-28</th><td>{totals[4]}</td></tr>
          <tr><th>29-54</th><td>{totals[5]}</td><th>55+</th><td>{totals[6]}</td><th>Afro</th><td>{totals[7]}</td><th>Indígena</th><td>{totals[8]}</td><th>ROM</th><td>{totals[9]}</td><th>LGTBI</th><td>{totals[10]}</td><th>Discapacidad</th><td>{totals[13]}</td></tr>
        </tbody>
      </table>
    </section>
  );
}

export default function OfficialAttendanceReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get('evento_id');

  const [meeting, setMeeting] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [groupBeneficiaries, setGroupBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [formatCode, setFormatCode] = useState('');

  useEffect(() => {
    async function loadData() {
      if (!meetingId) {
        setLoading(false);
        return;
      }

      try {
        const responseData = await getMeetingsAttendance(meetingId);
        if (responseData) {
          const meetingType = String(
            responseData.meeting?.type ?? responseData.meeting?.meeting_type ?? ''
          ).trim().toUpperCase();
          const beneficiariesData = meetingType.startsWith('COP_')
            ? []
            : await getBeneficiaries(null, meetingId);
          setMeeting(responseData.meeting);
          setAttendances(responseData.attendances || []);
          setGroupBeneficiaries(
            Array.isArray(beneficiariesData) ? beneficiariesData : []
          );
        } else {
          setMeeting(null);
          setAttendances([]);
          setGroupBeneficiaries([]);
        }
      } catch (error) {
        console.error('Error cargando asistencias del encuentro', error);
        setMeeting(null);
        setAttendances([]);
        setGroupBeneficiaries([]);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [meetingId]);

  const attendedRecords = useMemo(
    () => attendances.filter(isSignedAttendance),
    [attendances]
  );
  const sheets = useMemo(() => chunkRecords(attendedRecords, ROWS_PER_SHEET), [attendedRecords]);
  const fo093Sheets = useMemo(
    () => chunkRecords(attendedRecords, FO093_ROWS_PER_SHEET),
    [attendedRecords]
  );
  const allowedFormatOptions = useMemo(() => {
    const allowedCodes = new Set(
      getAllowedAttendanceFormatCodes(meeting, groupBeneficiaries)
    );
    return ATTENDANCE_FORMAT_OPTIONS.filter(option =>
      allowedCodes.has(option.value)
    );
  }, [meeting, groupBeneficiaries]);
  const activeFormatCode = allowedFormatOptions.some(
    option => option.value === formatCode
  )
    ? formatCode
    : allowedFormatOptions[0]?.value || '';

  const handlePrint = () => {
    if (!activeFormatCode) return;
    window.print();
  };

  const handlePdfDownload = async () => {
    if (downloading || !activeFormatCode) return;

    setShowDownloadOptions(false);
    setDownloading(true);
    try {
      const generator =
        activeFormatCode === 'FO-GINF-093'
          ? generateFO093Pdf
          : activeFormatCode === 'FO-GINF-086'
            ? generateFO086Pdf
            : generateAttendancePdf;
      await generator({
        meeting,
        meetingGroup,
        meetingSchool,
        sheets:
          activeFormatCode === 'FO-GINF-093' ||
          activeFormatCode === 'FO-GINF-086'
            ? fo093Sheets
            : sheets,
        attendedRecords,
        formatCode: activeFormatCode
      });
    } catch (error) {
      console.error('Error descargando el listado oficial en PDF', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleExcelDownload = () => {
    if (downloading || !activeFormatCode) return;

    setShowDownloadOptions(false);
    setDownloading(true);
    try {
      generateCharacterizationExcel({
        meeting,
        attendedRecords,
        formatCode: activeFormatCode
      });
    } catch (error) {
      console.error('Error descargando el listado oficial en Excel', error);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="report-loading">
        <div className="spinner"></div>
        <p>Cargando información del listado de asistencia...</p>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="report-error">
        <h3>Encuentro no encontrado</h3>
        <p>Por favor especifique un ID de encuentro válido en la URL (?evento_id=XXX).</p>
      </div>
    );
  }

  const meetingGroup =
    meeting.group ||
    meeting.activityGroup ||
    meeting.activity_group ||
    {};
  const meetingSchool =
    meeting.school ||
    meetingGroup.school ||
    (
      meeting.activityGroup || meeting.activity_group
        ? {
            name:
              meetingGroup.place ||
              meetingGroup.activity?.name ||
              meetingGroup.code,
            address: meetingGroup.address || '',
            municipality: { name: meetingGroup.comuna },
            neighborhood: { name: meetingGroup.barrio },
            organization: meetingGroup.activity?.line,
            user: meetingGroup.user
          }
        : {}
    );

  return (
    <div className="official-report-body">
      {showDownloadOptions && (
        <div
          className="attendance-download-modal no-print"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowDownloadOptions(false);
          }}
        >
          <div
            className="attendance-download-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attendance-download-modal-title"
          >
            <button
              type="button"
              className="attendance-download-modal__close"
              aria-label="Cerrar"
              onClick={() => setShowDownloadOptions(false)}
            >
              ×
            </button>
            <p id="attendance-download-modal-title">
              ¿En qué formato deseas descargar el listado?
            </p>
            <div className="attendance-download-modal__options">
              <button type="button" onClick={handlePdfDownload}>PDF</button>
              <button type="button" onClick={handleExcelDownload}>Excel</button>
            </div>
          </div>
        </div>
      )}
      <div className="print-controls no-print">
        <div className="controls-wrapper">
          <div className="format-control">
            <label htmlFor="attendance-format">
              Listado de Asistencia Oficial - Formato
            </label>
            <select
              id="attendance-format"
              value={activeFormatCode}
              onChange={(event) => setFormatCode(event.target.value)}
              disabled={!activeFormatCode}
            >
              {!activeFormatCode && (
                <option value="">Sin formato disponible</option>
              )}
              {allowedFormatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="controls-actions">
            {location.state?.backTo && (
              <button
                type="button"
                onClick={() => {
                  const backTo = location.state.backTo;
                  navigate(typeof backTo === 'string' ? backTo : backTo.pathname, {
                    state: typeof backTo === 'string' ? undefined : backTo.state
                  });
                }}
                className="btn-print"
              >
                <span className="material-symbols-sharp">arrow_back</span>
                <span>{location.state?.backLabel || 'Volver a encuentros'}</span>
              </button>
            )}
            <button
              onClick={handlePrint}
              className="btn-print"
              disabled={!activeFormatCode}
            >
              <span className="material-symbols-sharp">print</span>
              <span>Imprimir Formato</span>
            </button>
            <button
              onClick={() => setShowDownloadOptions(true)}
              className="btn-download"
              disabled={downloading || !activeFormatCode}
            >
              <span className="material-symbols-sharp">download</span>
              <span>{downloading ? 'Preparando descarga...' : 'Descargar Formato'}</span>
            </button>
          </div>
        </div>
      </div>

      {!activeFormatCode ? (
        <div className="report-error">
          <h3>Formato no disponible</h3>
          <p>
            El rol responsable del grupo y su población no tienen un formato
            de asistencia asignado.
          </p>
        </div>
      ) : (
        <div
          className={`printable-page ${
            activeFormatCode === 'FO-GINF-093' ||
            activeFormatCode === 'FO-GINF-086'
              ? 'fo093-printable'
              : 'fo083-printable'
          }`}
        >
        {activeFormatCode === 'FO-GINF-086' ? (
          fo093Sheets.map((records, index) => (
            <FO086Sheet
              key={index}
              pageIndex={index}
              records={records}
              meeting={meeting}
              meetingGroup={meetingGroup}
              meetingSchool={meetingSchool}
            />
          ))
        ) : (
          (activeFormatCode === 'FO-GINF-093' ? fo093Sheets : sheets).map((records, index) => (
            activeFormatCode === 'FO-GINF-093' ? (
            <FO093Sheet
              key={index}
              pageIndex={index}
              records={records}
              meeting={meeting}
              meetingGroup={meetingGroup}
              meetingSchool={meetingSchool}
            />
          ) : (
          <AttendanceSheet
            key={index}
            pageIndex={index}
            totalPages={sheets.length}
            records={records}
            meeting={meeting}
            meetingGroup={meetingGroup}
            meetingSchool={meetingSchool}
            formatCode={activeFormatCode}
          />
            )
          ))
        )}
        </div>
      )}
    </div>
  );
}
